import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import useTabDeepLink from '../hooks/useTabDeepLink';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid, LineChart, Line, ComposedChart, LabelList } from "recharts";
import { useLang } from "../context/LanguageContext";


// ── COLOURS ───────────────────────────────────────────────────────────────────
const CC = {PMI:"#2563eb",PBI:"#d97706",PII:"#16a34a",OSI:"#7c3aed",PPI:"#dc2626",SUPPORT:"#64748b",CORPORATE:"#0891b2"};
const MC = ["#2563eb","#d97706","#16a34a","#7c3aed","#dc2626","#64748b","#0891b2","#db2777","#65a30d","#9333ea"];
const gc = (n) => CC[n] || "#64748b";
const agg=(arr,key)=>{const m={};arr.forEach(d=>{m[d[key]]=(m[d[key]]||0)+d.TotalHours;});return Object.entries(m).map(([name,val])=>({name,val:Math.round(val)})).sort((a,b)=>b.val-a.val);};

// ── HELPERS ───────────────────────────────────────────────────────────────────
const fmtH=(h)=>h>=1?`${Math.round(h).toLocaleString()}h`:`${Math.round(h*60)}m`;
const fmtHMS=(decimalHours)=>{
  if(decimalHours<=0) return "0:00:00";
  const totalSeconds=Math.round(decimalHours*3600);
  const hours=Math.floor(totalSeconds/3600);
  const minutes=Math.floor((totalSeconds%3600)/60);
  const seconds=totalSeconds%60;
  return `${hours}:${String(minutes).padStart(2,'0')}:${String(seconds).padStart(2,'0')}`;
};
const HOURS_PER_DAY=8; // asumsi default 1 hari kerja = 8 jam
const fmtDays=(decimalHours,hoursPerDay=HOURS_PER_DAY)=>{
  if(decimalHours<=0) return "0d";
  const hpd=hoursPerDay>0?hoursPerDay:HOURS_PER_DAY;
  return `${(decimalHours/hpd).toFixed(2)}d`;
};
// Petakan periode bulanan "YYYY-MM" → trimester "YYYY-Tn" (1/3 tahun = 4 bulan).
// Bln 01-04 → T1, 05-08 → T2, 09-12 → T3. Input non-YYYY-MM dikembalikan apa adanya.
const periodThird=(p)=>{
  if(!/^\d{4}-\d{2}$/.test(p||"")) return p;
  const year=p.slice(0,4);
  const m=parseInt(p.slice(5,7),10);
  const t=m<=4?1:(m<=8?2:3);
  return `${year}-T${t}`;
};

// ── LIGHT THEME ───────────────────────────────────────────────────────────────
const S={
  page:{background:"#f1f5f9",minHeight:"100vh",fontFamily:"'Segoe UI',Arial,sans-serif",color:"#1e293b"},
  hdr:{background:"linear-gradient(135deg,rgb(var(--brand-700)) 0%,rgb(var(--brand-500)) 100%)",padding:"14px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,boxShadow:"0 2px 8px rgba(0,0,0,.15)"},
  filters:{background:"#fff",borderBottom:"1px solid #e2e8f0",padding:"10px 24px",display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"},
  tabs:{borderBottom:"2px solid #e2e8f0",display:"flex",padding:"0 24px",background:"#fff"},
  content:{padding:"20px 24px"},
  card:{background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:"18px 20px",boxShadow:"0 1px 4px rgba(0,0,0,.06)"},
  cardT:{fontSize:10,fontWeight:700,color:"#94a3b8",letterSpacing:2,textTransform:"uppercase",marginBottom:14},
  grid2:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16},
  grid3:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(210px,1fr))",gap:12},
};
const TT={contentStyle:{background:"#1e293b",border:"1px solid #334155",borderRadius:8,fontSize:12},labelStyle:{color:"#94a3b8"},itemStyle:{color:"#f1f5f9"}};

// ── CHART HELPERS (gaya SDP Cloud) ───────────────────────────────────────────
const BRAND="rgb(var(--brand-600))";
const CHART_MC=[BRAND,"#d97706","#16a34a","#7c3aed","#dc2626","#0891b2","#db2777","#65a30d","#9333ea","#0ea5e9"];
const AX={axisLine:false,tickLine:false};
const PILL={fontSize:11,fontWeight:700,color:"rgb(var(--brand-700))",background:"rgb(var(--brand-50))",padding:"3px 10px",borderRadius:999,letterSpacing:0,textTransform:"none",whiteSpace:"nowrap"};
const HEAD={...S.cardT,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8};
const axK=(v)=>v>=1000?(v/1000).toFixed(v>=10000?0:1)+"k":Math.round(v);
const fmtCompactH=(v)=>v>=10000?`${(v/1000).toFixed(1)}k h`:`${Math.round(v).toLocaleString()} h`;
const NoData=()=>(<div style={{fontSize:12,color:"#94a3b8",padding:"28px 0",textAlign:"center"}}>No data</div>);

// Bar horizontal tanpa sumbu + label "nilai · %" di ujung bar.
function HBarChart({data,dataKey="val",nameKey="name",color=BRAND,colorFor,nameWidth=120,showPct=true,onBarClick,tipName="Hours",unit="h"}){
  if(!data||!data.length) return <NoData/>;
  const isPct=unit==="pct";
  if(isPct) showPct=false;
  const tot=data.reduce((a,d)=>a+(d[dataKey]||0),0);
  const pc=(v)=>tot?Math.round((v/tot)*1000)/10:0;
  const lbl=(v)=>isPct?`${(v||0).toFixed(1)}%`:showPct?`${fmtH(v||0)} · ${pc(v||0)}%`:fmtH(v||0);
  return(
    <ResponsiveContainer width="100%" height={Math.max(140,data.length*30+16)}>
      <BarChart data={data} layout="vertical" margin={{top:4,left:4,right:showPct?128:64,bottom:4}} barCategoryGap={6}>
        <XAxis type="number" hide domain={[0,"dataMax"]}/>
        <YAxis type="category" dataKey={nameKey} width={nameWidth} interval={0} tick={{fontSize:11.5,fill:"#334155"}} {...AX}/>
        <Tooltip {...TT} cursor={{fill:"rgba(148,163,184,.12)"}}
          formatter={(v,n,p)=>[isPct?`${(v||0).toFixed(1)}% of standard (5d x 8h)`:`${(v||0).toLocaleString(undefined,{maximumFractionDigits:1})} hrs (${pc(v||0)}%)`,p&&p.payload&&p.payload.Company?`${isPct?'Utilisation':tipName} · ${p.payload.Company}`:(isPct?'Utilisation':tipName)]}/>
        <Bar dataKey={dataKey} radius={[0,4,4,0]} minPointSize={2} onClick={onBarClick} style={onBarClick?{cursor:"pointer"}:undefined}>
          {data.map((d,i)=><Cell key={i} fill={colorFor?colorFor(d,i):color}/>)}
          <LabelList dataKey={dataKey} position="right" offset={8} formatter={lbl} style={{fontSize:11,fontWeight:700,fill:"#334155"}}/>
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// Donut dengan total di tengah + legend berisi nilai, % dan bar tipis.
function DonutLegend({data,nameKey="name",valKey="val",colorFor,centerLabel="Total Hours",size=200,onPick}){
  if(!data||!data.length) return <NoData/>;
  const total=data.reduce((a,x)=>a+(x[valKey]||0),0);
  const pct=(v)=>total?Math.round((v/total)*1000)/10:0;
  const col=(d,i)=>colorFor?colorFor(d,i):CHART_MC[i%CHART_MC.length];
  const ir=Math.round(size*0.31), or=Math.round(size*0.46);
  return(
    <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:16}}>
      <div style={{position:"relative",flex:`0 0 ${size}px`,height:size,margin:"0 auto"}}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey={valKey} nameKey={nameKey} cx="50%" cy="50%" innerRadius={ir} outerRadius={or}
              paddingAngle={data.length>1?2:0} cornerRadius={4} stroke="none"
              style={onPick?{cursor:"pointer"}:undefined} onClick={onPick?(d)=>d&&onPick(d.payload||d):undefined}>
              {data.map((e,i)=><Cell key={i} fill={col(e,i)}/>)}
            </Pie>
            <Tooltip {...TT} formatter={(v,n)=>[`${Math.round(v).toLocaleString()} hrs (${pct(v)}%)`,n]}/>
          </PieChart>
        </ResponsiveContainer>
        <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
          <div style={{fontSize:size>=200?24:20,fontWeight:800,color:"#0f172a",lineHeight:1}}>{fmtCompactH(total)}</div>
          <div style={{fontSize:10,color:"#64748b",fontWeight:600,letterSpacing:.8,textTransform:"uppercase",marginTop:4}}>{centerLabel}</div>
        </div>
      </div>
      <div style={{flex:"1 1 200px",display:"flex",flexDirection:"column",gap:2,maxHeight:Math.max(size,240),overflowY:"auto",paddingRight:4}}>
        {data.map((d,i)=>{const v=d[valKey]||0;return(
          <div key={d[nameKey]} onClick={onPick?()=>onPick(d):undefined}
            style={{cursor:onPick?"pointer":"default",padding:"5px 8px",borderRadius:8}}
            onMouseEnter={e=>{e.currentTarget.style.background="#f8fafc";}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
            <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}>
              <span style={{width:10,height:10,borderRadius:3,background:col(d,i),flexShrink:0}}/>
              <span style={{flex:1,color:"#334155",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d[nameKey]}</span>
              <span style={{fontWeight:800,color:"#0f172a"}}>{Math.round(v).toLocaleString()}h</span>
              <span style={{color:"#94a3b8",width:44,textAlign:"right"}}>{pct(v)}%</span>
            </div>
            <div style={{height:4,background:"#f1f5f9",borderRadius:4,marginTop:5,marginLeft:18}}>
              <div style={{width:`${pct(v)}%`,height:"100%",background:col(d,i),borderRadius:4}}/>
            </div>
          </div>
        );})}
      </div>
    </div>
  );
}

// ── MINI BAR (gaya legend SDP) ────────────────────────────────────────────────
function BarRow({label,val,max,color}){
  const p=max>0?Math.round((val/max)*100):0;
  return(
    <div style={{marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}>
        <span style={{width:8,height:8,borderRadius:2,background:color,flexShrink:0}}/>
        <span style={{flex:1,color:"#334155",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</span>
        <span style={{fontWeight:800,color:"#0f172a"}}>{val.toLocaleString()}h</span>
      </div>
      <div style={{height:4,background:"#f1f5f9",borderRadius:4,marginTop:5,marginLeft:16}}>
        <div style={{width:`${p}%`,height:"100%",background:color,borderRadius:4}}/>
      </div>
    </div>
  );
}

// ── SORTABLE TH ───────────────────────────────────────────────────────────────
function SortTh({col,label,sortCol,sortDir,onSort}){
  const active=sortCol===col;
  return(<th onClick={()=>onSort(col)} style={{padding:"9px 12px",textAlign:"left",fontSize:10,fontWeight:700,letterSpacing:1,
    background:active?"rgb(var(--brand-50))":"#f8fafc",color:active?"rgb(var(--brand-700))":"#64748b",
    borderBottom:"2px solid #e2e8f0",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>
    {label}<span style={{color:active?"rgb(var(--brand-600))":"#cbd5e1",fontSize:9}}>{active?(sortDir===-1?" ▼":" ▲"):" ⇅"}</span>
  </th>);
}

// ── ANALYTICS TAB ─────────────────────────────────────────────────────────────
// Lapisan insight (bukan breakdown mentah), dihitung penuh di JS dari `rows`
// (basis = data terfilter TANPA filter periode tunggal, agar time-series jalan).
// Panel: KPI headline · tren+MA3 · top movers modul · pergeseran mix company
// (stacked 100%) · Pareto konsentrasi user · deteksi anomali (>1,4× rata-rata 3 bln).
function AnalyticsTab({rows,lang}){
  const L=(id,en)=>(lang==="id"||lang==="ID")?id:en;
  const axFmt=(v)=>v>=1000?(v/1000).toFixed(1)+"k":Math.round(v);
  const pct=(v)=>v==null?"—":(v>=0?"+":"")+v.toFixed(1)+"%";
  const deltaColor=(v)=>v==null?"#64748b":v>0?"#16a34a":"#dc2626";
  const th={padding:"8px 10px",textAlign:"left",fontSize:10,fontWeight:700,letterSpacing:1,background:"#f8fafc",color:"#64748b",borderBottom:"2px solid #e2e8f0",whiteSpace:"nowrap"};
  const td={padding:"8px 10px",fontSize:12,color:"#334155",borderBottom:"1px solid #f1f5f9"};

  const periodList=useMemo(()=>{
    const m=new Map();
    rows.forEach(d=>{
      const k=d.Period||"";
      if(!k) return;
      const e=m.get(k)||{key:k,total:0,users:new Set()};
      e.total+=d.TotalHours||0;
      if((d.TotalHours||0)>0) e.users.add(d.User);
      m.set(k,e);
    });
    return [...m.values()].map(e=>({key:e.key,total:e.total,users:e.users.size})).sort((a,b)=>a.key<b.key?-1:1);
  },[rows]);

  const lastP=periodList[periodList.length-1]||null;
  const prevP=periodList[periodList.length-2]||null;

  const head=useMemo(()=>{
    const grand=periodList.reduce((s,p)=>s+p.total,0);
    const mom=lastP&&prevP&&prevP.total>0?((lastP.total-prevP.total)/prevP.total)*100:null;
    const activeUsers=lastP?lastP.users:0;
    const avgPerUser=lastP&&lastP.users>0?lastP.total/lastP.users:null;
    return {grand,mom,activeUsers,avgPerUser};
  },[periodList,lastP,prevP]);

  const trend=useMemo(()=>periodList.map((p,i)=>{
    const w=periodList.slice(Math.max(0,i-2),i+1);
    return {period:p.key,total:Math.round(p.total),ma:Math.round(w.reduce((s,x)=>s+x.total,0)/w.length)};
  }),[periodList]);

  const byKeyInPeriod=(key,field)=>{
    const m={};
    rows.forEach(d=>{ if((d.Period||"")!==key) return; const n=d[field]||"—"; m[n]=(m[n]||0)+(d.TotalHours||0); });
    return m;
  };

  const movers=useMemo(()=>{
    if(!lastP||!prevP) return [];
    const a=byKeyInPeriod(prevP.key,"Module"), b=byKeyInPeriod(lastP.key,"Module");
    const keys=new Set([...Object.keys(a),...Object.keys(b)]);
    return [...keys].map(k=>{const p=a[k]||0,c=b[k]||0;return{k,prev:p,cur:c,delta:c-p,pct:p>0?((c-p)/p)*100:null};})
      .filter(x=>Math.abs(x.delta)>0.5).sort((x,y)=>Math.abs(y.delta)-Math.abs(x.delta)).slice(0,8);
  },[rows,lastP,prevP]); // eslint-disable-line

  const companies=useMemo(()=>Array.from(new Set(rows.map(d=>d.Company))).filter(Boolean),[rows]);
  const mix=useMemo(()=>periodList.map(p=>{
    const row={period:p.key}; let tot=0; const raw={};
    companies.forEach(c=>{ raw[c]=rows.filter(d=>(d.Period||"")===p.key&&d.Company===c).reduce((s,d)=>s+(d.TotalHours||0),0); tot+=raw[c]; });
    companies.forEach(c=>{ row[c]=tot>0?(raw[c]/tot)*100:0; });
    return row;
  }),[periodList,companies,rows]);

  const pareto=useMemo(()=>{
    const m={};
    rows.forEach(d=>{ const u=d.User||"—"; m[u]=(m[u]||0)+(d.TotalHours||0); });
    const arr=Object.entries(m).map(([name,val])=>({name,val})).filter(x=>x.val>0).sort((a,b)=>b.val-a.val);
    const grand=arr.reduce((s,x)=>s+x.val,0);
    let cum=0;
    const top=arr.slice(0,12).map(x=>{cum+=x.val;return{name:x.name.length>14?x.name.slice(0,13)+"…":x.name,full:x.name,val:Math.round(x.val),cumPct:grand>0?(cum/grand)*100:0};});
    let c=0,n80=0; for(const x of arr){c+=x.val;n80++;if(grand>0&&c/grand>=0.8)break;}
    return {rows:top,n80,total:arr.length};
  },[rows]);

  const anomalies=useMemo(()=>{
    const out=[];
    periodList.forEach((p,i)=>{ if(i<3) return; const base=periodList.slice(i-3,i).reduce((s,x)=>s+x.total,0)/3; if(base>0&&p.total>base*1.4) out.push({p,base,ratio:p.total/base,pct:((p.total-base)/base)*100}); });
    return out.reverse();
  },[periodList]);

  if(!periodList.length) return <div style={S.card}><div style={{...S.cardT,marginBottom:0}}>{L("Tidak ada data untuk filter ini.","No data for this filter.")}</div></div>;

  const kpi=(label,value,sub,color)=>(
    <div style={S.card}>
      <div style={S.cardT}>{label}</div>
      <div style={{fontSize:24,fontWeight:800,color:color||"#1e293b"}}>{value}</div>
      {sub!=null&&<div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>{sub}</div>}
    </div>
  );

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* Headline KPI */}
      <div style={S.grid3}>
        {kpi(L("Total jam (rentang)","Total hours (range)"),Math.round(head.grand).toLocaleString()+"h",`${periodList.length} ${L("periode","periods")}`)}
        {kpi(L("MoM terakhir","Latest MoM"),pct(head.mom),lastP&&prevP?`${lastP.key} vs ${prevP.key}`:"—",deltaColor(head.mom))}
        {kpi(L("User aktif (terakhir)","Active users (latest)"),head.activeUsers.toLocaleString(),lastP?lastP.key:"—")}
        {kpi(L("Rata-rata jam/user","Avg hours/user"),head.avgPerUser!=null?Math.round(head.avgPerUser).toLocaleString()+"h":"—",L("periode terakhir","latest period"))}
      </div>

      {/* Tren + MA3 */}
      <div style={S.card}>
        <div style={S.cardT}>{L("Tren jam bulanan + rata-rata 3 bln","Monthly hours trend + 3-mo avg")}</div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={trend} margin={{top:6,right:16,bottom:6,left:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
            <XAxis dataKey="period" tick={{fill:"#64748b",fontSize:11}}/>
            <YAxis tick={{fill:"#94a3b8",fontSize:10}} tickFormatter={axFmt}/>
            <Tooltip {...TT} formatter={v=>[`${v.toLocaleString()} hrs`]}/>
            <Legend iconSize={8} wrapperStyle={{fontSize:11}}/>
            <Line type="monotone" dataKey="total" name={L("Total","Total")} stroke={BRAND} strokeWidth={2} dot={{r:3}}/>
            <Line type="monotone" dataKey="ma" name={L("Rata-rata 3 bln","3-mo avg")} stroke="#d97706" strokeWidth={2} strokeDasharray="5 4" dot={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={S.grid2}>
        {/* Top movers modul */}
        <div style={S.card}>
          <div style={S.cardT}>{L("Top movers — modul","Top movers — modules")}{lastP&&prevP?` (${lastP.key} vs ${prevP.key})`:""}</div>
          {movers.length?(
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr>
                <th style={th}>{L("Modul","Module")}</th>
                <th style={{...th,textAlign:"right"}}>{L("Sblm","Prev")}</th>
                <th style={{...th,textAlign:"right"}}>{L("Skrg","Cur")}</th>
                <th style={{...th,textAlign:"right"}}>Δ</th>
                <th style={{...th,textAlign:"right"}}>Δ%</th>
              </tr></thead>
              <tbody>{movers.map(m=>(
                <tr key={m.k}>
                  <td style={td}>{m.k}</td>
                  <td style={{...td,textAlign:"right"}}>{Math.round(m.prev).toLocaleString()}h</td>
                  <td style={{...td,textAlign:"right"}}>{Math.round(m.cur).toLocaleString()}h</td>
                  <td style={{...td,textAlign:"right",fontWeight:700,color:deltaColor(m.delta)}}>{(m.delta>0?"+":"−")+Math.abs(Math.round(m.delta)).toLocaleString()}h</td>
                  <td style={{...td,textAlign:"right",color:deltaColor(m.pct)}}>{pct(m.pct)}</td>
                </tr>
              ))}</tbody>
            </table>
          ):<div style={{fontSize:12,color:"#94a3b8",padding:8}}>{L("Butuh ≥2 periode untuk membandingkan.","Need ≥2 periods to compare.")}</div>}
        </div>

        {/* Deteksi anomali */}
        <div style={S.card}>
          <div style={S.cardT}>{L("Deteksi anomali (lonjakan jam)","Anomaly detection (hour spikes)")}</div>
          {anomalies.length?anomalies.map(a=>(
            <div key={a.p.key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,padding:"8px 12px",marginBottom:8}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#1e293b"}}>{a.p.key}</div>
                <div style={{fontSize:11,color:"#94a3b8"}}>{L("vs rata-rata 3 bln","vs 3-mo avg")} {Math.round(a.base).toLocaleString()}h</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:14,fontWeight:700,color:"#dc2626"}}>{Math.round(a.p.total).toLocaleString()}h</div>
                <div style={{fontSize:11,fontWeight:700,color:"#dc2626"}}>{a.ratio.toFixed(1)}× ({pct(a.pct)})</div>
              </div>
            </div>
          )):<div style={{fontSize:12,color:"#16a34a",padding:8}}>✓ {L("Tidak ada lonjakan signifikan (>1,4× rata-rata 3 bln).","No significant spikes (>1.4× the 3-mo average).")}</div>}
        </div>
      </div>

      {/* Pergeseran mix company (stacked 100%) */}
      <div style={S.card}>
        <div style={S.cardT}>{L("Pergeseran komposisi jam per company (100%)","Company hours mix shift (100%)")}</div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={mix} margin={{top:6,right:16,bottom:6,left:0}} stackOffset="expand">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
            <XAxis dataKey="period" tick={{fill:"#64748b",fontSize:11}}/>
            <YAxis tick={{fill:"#94a3b8",fontSize:10}} tickFormatter={v=>Math.round(v*100)+"%"} domain={[0,1]}/>
            <Tooltip {...TT} formatter={(v,n)=>[`${v.toFixed(1)}%`,n]}/>
            <Legend iconSize={8} wrapperStyle={{fontSize:11}}/>
            {companies.map(c=><Bar key={c} dataKey={c} stackId="a" fill={gc(c)}/>)}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Pareto konsentrasi user */}
      <div style={S.card}>
        <div style={S.cardT}>{L("Konsentrasi jam per user (Pareto)","Hours concentration by user (Pareto)")}</div>
        <div style={{fontSize:12,color:"#64748b",marginBottom:10}}>
          {L("Sekitar","About")} <strong>{pareto.n80}</strong> {L("dari","of")} {pareto.total} {L("user menyumbang ~80% total jam.","users drive ~80% of total hours.")}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={pareto.rows} margin={{top:6,right:12,bottom:60,left:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
            <XAxis dataKey="name" tick={{fill:"#64748b",fontSize:10}} angle={-40} textAnchor="end" interval={0} height={70}/>
            <YAxis yAxisId="l" tick={{fill:"#94a3b8",fontSize:10}} tickFormatter={axFmt}/>
            <YAxis yAxisId="r" orientation="right" domain={[0,100]} tick={{fill:"#94a3b8",fontSize:10}} tickFormatter={v=>v+"%"}/>
            <Tooltip {...TT} formatter={(v,n,it)=>it&&it.dataKey==="cumPct"?[v.toFixed(1)+"%",L("Kumulatif","Cumulative")]:[`${v.toLocaleString()} hrs`,L("Jam","Hours")]}/>
            <Bar yAxisId="l" dataKey="val" name={L("Jam","Hours")} fill={BRAND} radius={[4,4,0,0]}/>
            <Line yAxisId="r" type="monotone" dataKey="cumPct" name={L("Kumulatif %","Cumulative %")} stroke="#d97706" strokeWidth={2} dot={{r:2}}/>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const USRUTL_CONN_BY_LS = { ID: 13, JB: 16, KL: 20 };

// ── MAIN ──────────────────────────────────────────────────────────────────────
// PERUBAHAN 1: tambah "props" agar bisa menerima onDataChange dari parent
export default function Dashboard(props){
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  const SAP_TAB_IDS = ['overview','company','module','users','table','pivot','analytics','anomali','capacity','license','utilisation'];
  const [rawData,setRawData]=useState([]);
  const [fileName,setFileName]=useState("");
  // ── DB State ──────────────────────────────────────────────────────────────
  const [dbStatus,setDbStatus]=useState(null);   // { exists, count, periods, sources }
  const [dbMsg,setDbMsg]=useState(null);          // { type: "ok"|"err", text }
  const [isLoading,setIsLoading]=useState(true);  // loading DB on mount
  const API_BASE=window.__API_BASE__||"";
  const getToken=()=>localStorage.getItem("auth_token")||localStorage.getItem("token")||"";
  const [fComp,setFComp]=useState("ALL");
  const [fLandscape,setFLandscape]=useState("ID"); // Default: ID landscape
  // Tab Login Anomalies / Peak Concurrent / License Review membaca log sesi
  // (sap_usr_utl_raw) per SISTEM, jadi ikut filter landscape: ID=conn 13,
  // JB=conn 16, KL=conn 20 (P01 KL client 100). ALL tidak bisa digabung.
  const USRUTL_CONN = USRUTL_CONN_BY_LS[fLandscape] || 0;
  const [fMod,setFMod]=useState("ALL");
  const [fPer,setFPer]=useState("ALL");
  const [fPers,setFPers]=useState([]); // Multiselect periods dengan CTRL
  const [metric,setMetric]=useState("hours"); // "hours" | "util" — toggle Hours <-> Utilisation
  const [fPerMode,setFPerMode]=useState("third"); // Mode chip filter PERIOD: "month" | "third" (trimester T1/T2/T3)
  const [expandedUser,setExpandedUser]=useState(null); // Expandable row detail per PERIOD
  const [fLic,setFLic]=useState("ALL");
  const [fYear,setFYear]=useState("ALL");       // legacy — tidak dipakai filter
  const [fYears,setFYears]=useState([]);        // multiselect: [] = All Years
  const [tab,setTab]=useState(()=>{ const p=searchParams.get('tab'); return (p&&SAP_TAB_IDS.includes(p))?p:'overview'; });
  useTabDeepLink('sap_usage', (p) => SAP_TAB_IDS.includes(p), setTab);

  // -- USR-UTL helpers (moved from SAP Basis Monitor) ----------------------
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const SectionError = ({ msg }) => (
    <div style={{ padding: 12, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: 13 }}>{msg}</div>
  );
  const api = useCallback(async (path) => {
    const r = await fetch(API_BASE + path, { headers: { Authorization: 'Bearer ' + getToken() } });
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  }, []);

  // -- Anomali login (sumber: sap_usr_utl_raw, bukan snapshot health) ------
  // Dimuat MALAS, hanya saat tab Anomali dibuka. Query-nya menyapu 90 hari
  // (~150 ribu baris di P01), jadi tidak pantas ikut jalan setiap halaman
  // dibuka seperti KPI health yang lain.
  const [anom, setAnom] = useState(null);
  const [anomErr, setAnomErr] = useState('');
  const [anomLoading, setAnomLoading] = useState(false);
  const [anomDays, setAnomDays] = useState(90);
  const loadAnom = useCallback(async (cid, days) => {
    const target = cid || USRUTL_CONN;
    if (!target) { setAnom(null); return; }
    setAnomLoading(true);
    try {
      const d = await api(`/api/sap/usr-utl/anomaly?connectionId=${encodeURIComponent(target)}&days=${days || 90}`);
      setAnom(d); setAnomErr('');
    } catch (e) { setAnom(null); setAnomErr(e.message || String(e)); }
    finally { setAnomLoading(false); }
  }, [USRUTL_CONN, api]);
  // Ganti profil koneksi = buang hasil lama. Tanpa ini, angka P01 sempat
  // terlihat seolah milik D01 sampai fetch berikutnya selesai.
  useEffect(() => { setAnom(null); setAnomErr(''); }, [USRUTL_CONN]);
  useEffect(() => {
    if (tab === 'anomali' && USRUTL_CONN && !anom && !anomLoading && !anomErr) loadAnom(USRUTL_CONN, anomDays);
  }, [tab, USRUTL_CONN, anom, anomLoading, anomErr, anomDays, loadAnom]);

  // -- Capacity: concurrency asli dari sap_usr_utl_raw --------------------
  // Dimuat malas seperti Anomali. Query harian (per tanggal x jam) 24x lebih
  // berat dari yang per jam, jadi jangan pernah ikut load halaman.
  const [cap, setCap] = useState(null);
  const [capErr, setCapErr] = useState('');
  const [capLoading, setCapLoading] = useState(false);
  const loadCap = useCallback(async (cid, days) => {
    const target = cid || USRUTL_CONN;
    if (!target) { setCap(null); return; }
    setCapLoading(true);
    try {
      const d = await api(`/api/sap/usr-utl/capacity?connectionId=${encodeURIComponent(target)}&days=${days || 90}`);
      setCap(d); setCapErr('');
    } catch (e) { setCap(null); setCapErr(e.message || String(e)); }
    finally { setCapLoading(false); }
  }, [USRUTL_CONN, api]);
  useEffect(() => { setCap(null); setCapErr(''); }, [USRUTL_CONN]);
  useEffect(() => {
    if (tab === 'capacity' && USRUTL_CONN && !cap && !capLoading && !capErr) loadCap(USRUTL_CONN, 90);
  }, [tab, USRUTL_CONN, cap, capLoading, capErr, loadCap]);

  // -- License review: master user x pemakaian nyata ----------------------
  const [lic, setLic] = useState(null);
  const [dealEdit, setDealEdit] = useState(false);
  const [licErr, setLicErr] = useState('');
  const [licLoading, setLicLoading] = useState(false);
  const loadLic = useCallback(async (cid) => {
    const target = cid || USRUTL_CONN;
    if (!target) { setLic(null); return; }
    setLicLoading(true);
    try {
      const d = await api(`/api/sap/usr-utl/license?connectionId=${encodeURIComponent(target)}&days=180`);
      setLic(d); setLicErr('');
    } catch (e) { setLic(null); setLicErr(e.message || String(e)); }
    finally { setLicLoading(false); }
  }, [USRUTL_CONN, api]);
  useEffect(() => { setLic(null); setLicErr(''); }, [USRUTL_CONN]);
  useEffect(() => {
    if (tab === 'license' && USRUTL_CONN && !lic && !licLoading && !licErr) loadLic(USRUTL_CONN);
  }, [tab, USRUTL_CONN, lic, licLoading, licErr, loadLic]);

  // Export dibuat dari data yang SUDAH tampil, bukan panggilan server baru.
  // Dengan begitu isi file dijamin sama persis dengan yang dilihat di layar -
  // penting karena file ini dikirim ke perusahaan untuk dijadikan keputusan.
  const exportLic = useCallback(() => {
    if (!lic) return;
    const head = ['Username', 'Full name', 'Company', 'Department', 'License', 'Last login', 'Hours 180d', 'Util % (5d x 8h)', 'Active days', 'Recommendation'];
    const esc = (v) => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const body = lic.users.map((u) => [u.username, u.full_name, u.company, u.department,
      u.license, u.last_login || 'NEVER', u.hours, u.util_pct, u.active_days, u.verdict].map(esc).join(','));
    // BOM ﻿ wajib: tanpa itu Excel membaca UTF-8 sebagai ANSI dan nama
    // dengan karakter non-ASCII jadi rusak.
    const blob = new Blob(['﻿' + [head.map(esc).join(',')].concat(body).join('\r\n')],
      { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sap-license-review-conn' + USRUTL_CONN + '-' + new Date().toISOString().slice(0, 10) + '.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [lic, USRUTL_CONN]);

  const [search,setSearch]=useState("");
  const [sortCol,setSortCol]=useState("hours");
  const [sortDir,setSortDir]=useState(-1);
  const [topMode,setTopMode]=useState("lowest");
  const [topN,setTopN]=useState(15);
  const [selModule,setSelModule]=useState(null);
  const [pivotModuleFilter,setPivotModuleFilter]=useState("ALL");  // Pivot table module filter
  const [pivotSearch,setPivotSearch]=useState("");  // Pivot table search
  const [pivotUnit,setPivotUnit]=useState("hours"); // Pivot table display unit: "hours" | "days"
  const [pivotHoursPerDay,setPivotHoursPerDay]=useState(8); // jam kerja per hari (untuk konversi ke hari)
  const [pivotSort,setPivotSort]=useState({col:"user",dir:1}); // sort pivot: col="user"|"module"|"total"|<label kolom>, dir=1 asc/-1 desc
  const [pivotPeriodMode,setPivotPeriodMode]=useState("third"); // Pivot period grouping: "month" | "third"

  const handleSort=(col)=>{if(sortCol===col)setSortDir(d=>d*-1);else{setSortCol(col);setSortDir(col==="hours"?-1:1);}};

  // ── DB Helpers ─────────────────────────────────────────────────────────────
  const checkDbStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/sap-usage/status`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (r.ok) { const d = await r.json(); setDbStatus(d); }
    } catch { /* offline / not logged in */ }
  }, []); // eslint-disable-line




  // Auto-load dari DB saat pertama kali mount
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const r = await fetch(`${API_BASE}/api/sap-usage/data`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const d = await r.json();
        if (r.ok && d.data?.length) {
          setRawData(d.data);
          setFileName(`Database (${d.total} records)`);
        } else {
          setRawData([]);
          setFileName("");
          setDbMsg({ type: "err", text: "Belum ada data di DB." });
        }
      } catch {
        setRawData([]);
        setFileName("");
        setDbMsg({ type: "err", text: "Tidak bisa terhubung ke server." });
      } finally {
        setIsLoading(false);
        checkDbStatus();
      }
    })();
  }, []); // eslint-disable-line

  const years=useMemo(()=>["ALL",...Array.from(new Set(rawData.map(d=>(d.Period||"").substring(0,4)).filter(y=>y.length===4))).sort()],[rawData]);
  const yearInitRef=useRef(false);
  useEffect(()=>{
    if(yearInitRef.current||!rawData.length) return;
    yearInitRef.current=true;
    const ys=years.filter(y=>y!=="ALL");
    if(ys.length) setFYears([ys[ys.length-1]]);
  },[rawData,years]);
  const landscapes=useMemo(()=>["ALL",...Array.from(new Set(rawData.map(d=>d.Landscape||"PROD"))).sort()],[rawData]);
  const companies=useMemo(()=>["ALL",...Array.from(new Set(rawData.map(d=>d.Company))).sort()],[rawData]);
  const modules=useMemo(()=>["ALL",...Array.from(new Set(rawData.map(d=>d.Module))).sort()],[rawData]);
  
  // ── RELEVANT COMPANIES berdasarkan LANDSCAPE filter ──
  const relevantCompanies=useMemo(()=>{
    if(fLandscape==="ALL") return companies;
    const filtered=rawData.filter(d=>d.Landscape===fLandscape);
    return ["ALL",...Array.from(new Set(filtered.map(d=>d.Company))).sort()];
  },[rawData,companies,fLandscape]);
  
  // ── RELEVANT MODULES berdasarkan LANDSCAPE filter ──
  const relevantModules=useMemo(()=>{
    if(fLandscape==="ALL") return ["ALL",...Array.from(new Set(rawData.map(d=>d.Module))).sort()];
    const filtered=rawData.filter(d=>d.Landscape===fLandscape);
    return ["ALL",...Array.from(new Set(filtered.map(d=>d.Module))).sort()];
  },[rawData,fLandscape]);
  
  const periods=useMemo(()=>{
    const base=fYears.length===0?rawData:rawData.filter(d=>fYears.includes((d.Period||"").substring(0,4)));
    return["ALL",...Array.from(new Set(base.map(d=>d.Period))).sort()];
  },[rawData,fYears]);
  // Default saat halaman dibuka: filter PERIOD = trimester terakhir (sekali saja, setelah data termuat)
  const initTrimesterDone=useRef(false);
  useEffect(()=>{
    if(initTrimesterDone.current) return;
    const months=periods.filter(p=>/^\d{4}-\d{2}$/.test(p||""));
    if(months.length===0) return;
    initTrimesterDone.current=true;
    const lastT=periodThird(months[months.length-1]);
    setFPer("ALL");
    setFPers(months.filter(m=>periodThird(m)===lastT));
  },[periods]);
  // Kolom pivot: bulanan (default) atau trimester 1/3-tahun (T1/T2/T3)
  // Kolom pivot hanya periode yang lolos filter PERIOD (fPers multiselect / fPer single) — tanpa kolom kosong
  const periodCols=useMemo(()=>periods.filter(p=>p!=="ALL"&&(fPers.length>0?fPers.includes(p):(fPer==="ALL"||p===fPer))),[periods,fPer,fPers]);
  const thirdCols=useMemo(()=>Array.from(new Set(periodCols.map(periodThird))).sort(),[periodCols]);
  // Sort untuk tabel pivot: kolom string default naik, kolom angka default turun
  const onPivotSort=(key)=>setPivotSort(s=>s.col===key?{col:key,dir:-s.dir}:{col:key,dir:(key==="user"||key==="module")?1:-1});
  const pivotArrow=(key)=>pivotSort.col===key?(pivotSort.dir>0?" ▲":" ▼"):" ⇅";

  const filtered=useMemo(()=>rawData.filter(d=>
    (fLandscape==="ALL"||d.Landscape===fLandscape)&&(fComp==="ALL"||d.Company===fComp)&&(fMod==="ALL"||d.Module===fMod)&&
    (fPers.length===0?fPer==="ALL"||d.Period===fPer:fPers.includes(d.Period))&&(fLic==="ALL"||d.LicenseType===fLic)&&
    (fYears.length===0||fYears.includes((d.Period||"").substring(0,4)))&&
    (search===""||d.User.toLowerCase().includes(search.toLowerCase())||d.Dept.toLowerCase().includes(search.toLowerCase()))
  ),[rawData,fLandscape,fComp,fMod,fPer,fPers,fLic,fYears,search]);

  // Bulan untuk tab Utilisation vs Standard: ikut filter PERIOD, lalu YEAR.
  // Kosong = tidak ada filter → panel memakai jendela hari (30/90/180).
  const utMonths = useMemo(() => {
    if (fPers.length) return [...fPers].sort();
    if (fPer !== "ALL") return [fPer];
    if (fYears.length) return fYears.flatMap((y) => Array.from({ length: 12 }, (_, i) => y + '-' + String(i + 1).padStart(2, '0'))).sort();
    return [];
  }, [fPers, fPer, fYears]);

  // Basis Analytics: sama seperti `filtered` TAPI tanpa filter periode tunggal
  // (fPer/fPers) — agar tren lintas periode tetap terbentuk (analog yearItems).
  const anBase=useMemo(()=>rawData.filter(d=>
    (fLandscape==="ALL"||d.Landscape===fLandscape)&&(fComp==="ALL"||d.Company===fComp)&&(fMod==="ALL"||d.Module===fMod)&&
    (fLic==="ALL"||d.LicenseType===fLic)&&
    (fYears.length===0||fYears.includes((d.Period||"").substring(0,4)))&&
    (search===""||d.User.toLowerCase().includes(search.toLowerCase())||d.Dept.toLowerCase().includes(search.toLowerCase()))
  ),[rawData,fLandscape,fComp,fMod,fLic,fYears,search]);

  // PERUBAHAN 2: kirim data ke Primary Dashboard AI setiap filter berubah
  const activeFilters = [
    fLandscape !== "ALL" && ("Landscape: " + fLandscape),
    fComp !== "ALL" && ("Company: " + fComp),
    fYears.length > 0 && ("Year: " + fYears.join(", ")),
    (fPers.length > 0 ? ("Period: " + fPers.join(", ")) : (fPer !== "ALL" && ("Period: " + fPer))),
    fLic  !== "ALL" && ("License: " + fLic.replace("SAP Application ","")),
  ].filter(Boolean).join(" · ") || "No filters applied";

  useEffect(() => {
    if (props.onDataChange && filtered) {
      props.onDataChange({ rawData, filtered, fileName, activeFilters });
    }
  }, [filtered, fileName, activeFilters]); // eslint-disable-line

  // NOTE: Admin role detection removed auto-detection.
  // Admin role must be explicitly set via props.userRole or localStorage.userRole = 'ADMIN'

  const totalH=useMemo(()=>filtered.reduce((s,d)=>s+d.TotalHours,0),[filtered]);
  const totalU=useMemo(()=>new Set(filtered.map(d=>d.User)).size,[filtered]);
  const activeU=useMemo(()=>new Set(filtered.filter(d=>d.TotalHours>0).map(d=>d.User)).size,[filtered]);
  // Utilisation = jam / standar (hari kerja Senin-Jumat x 8 jam) per periode yang
  // DIPAKAI user (bulan tanpa baris untuk user itu tidak dihitung, jadi user baru
  // tidak terhukum). Bulan berjalan dihitung sampai kemarin. Libur belum dikurangi.
  const perStd=useCallback((p)=>{
    const m=/^(\d{4})-(\d{2})$/.exec(p||"");if(!m)return 0;
    const y=+m[1],mo=+m[2];const y0=new Date();y0.setDate(y0.getDate()-1);
    let n=0;for(let d=new Date(y,mo-1,1);d.getMonth()===mo-1&&d<=y0;d.setDate(d.getDate()+1)){const w=d.getDay();if(w!==0&&w!==6)n++;}
    return n*8;
  },[]);
  const aggUtil=useCallback((arr,key)=>{
    const m={};
    arr.forEach(d=>{const k=d[key];if(!m[k])m[k]={h:0,up:new Set()};m[k].h+=d.TotalHours;m[k].up.add(d.User+"|"+d.Period);});
    return Object.entries(m).map(([name,x])=>{let st=0;x.up.forEach(k=>{st+=perStd(k.split("|")[1]);});return{name,val:st?Math.round(x.h/st*1000)/10:0,hours:Math.round(x.h),std:st};}).sort((a,b)=>b.val-a.val);
  },[perStd]);
  const isUtil=metric==="util";
  const byComp=useMemo(()=>isUtil?aggUtil(filtered,"Company"):agg(filtered,"Company"),[filtered,isUtil,aggUtil]);
  const byMod=useMemo(()=>agg(filtered,"Module"),[filtered]);
  const byModU=useMemo(()=>isUtil?aggUtil(filtered,"Module"):byMod,[filtered,isUtil,aggUtil,byMod]);
  const overallUtil=useMemo(()=>{const x=aggUtil(filtered.map(d=>({...d,_all:"all"})),"_all")[0];return x?x.val:0;},[filtered,aggUtil]);
  const byLic=useMemo(()=>{const m={};filtered.forEach(d=>{m[d.LicenseType]=(m[d.LicenseType]||0)+d.TotalHours;});return Object.entries(m).map(([n,v])=>({name:n.replace("SAP Application ",""),val:Math.round(v)}));},[filtered]);
  const byPeriod=useMemo(()=>{
    const cos=Array.from(new Set(filtered.map(d=>d.Company)));
    const pers=Array.from(new Set(filtered.map(d=>d.Period))).sort();
    return pers.map(p=>{const row={period:p};cos.forEach(c=>{row[c]=Math.round(filtered.filter(d=>d.Period===p&&d.Company===c).reduce((s,d)=>s+d.TotalHours,0));});return row;});
  },[filtered]);
  const compKeys=useMemo(()=>Array.from(new Set(filtered.map(d=>d.Company))),[filtered]);
  const byPeriodT=useMemo(()=>byPeriod.map(r=>({...r,_total:compKeys.reduce((a,c)=>a+(r[c]||0),0)})),[byPeriod,compKeys]);

  const allUserHours=useMemo(()=>{const m={};filtered.forEach(d=>{if(!m[d.User])m[d.User]={User:d.User,Company:d.Company,Module:d.Module,hours:0,pers:new Set()};m[d.User].hours+=d.TotalHours;m[d.User].pers.add(d.Period);});const arr=Object.values(m).map(u=>{let st=0;u.pers.forEach(p=>{st+=perStd(p);});return{...u,std:st,util:st?Math.round(u.hours/st*1000)/10:0};});const k=isUtil?"util":"hours";return arr.sort((a,b)=>b[k]-a[k]);},[filtered,isUtil,perStd]);
  const topUsers=useMemo(()=>{const sorted=topMode==="highest"?allUserHours:[...allUserHours].reverse();return sorted.slice(0,topN);},[allUserHours,topMode,topN]);

  const tableData=useMemo(()=>{
    const m={};filtered.forEach(d=>{const k=`${d.User}|${d.Company}|${d.Module}|${d.LicenseType}`;if(!m[k])m[k]={...d,hours:0,_pers:new Set()};m[k].hours+=d.TotalHours;m[k]._pers.add(d.Period);});
    Object.values(m).forEach(r=>{let st=0;r._pers.forEach(p=>{st+=perStd(p);});r.std=st;r.util=st?Math.round(r.hours/st*1000)/10:0;});
    const colMap={user:"User",company:"Company",dept:"Dept",module:"Module",license:"LicenseType",lic:"Lic",hours:"hours"};
    const nk=isUtil?"util":"hours";
    return Object.values(m).sort((a,b)=>{const key=colMap[sortCol]||"hours";const va=sortCol==="hours"?a[nk]:(a[key]||"").toLowerCase();const vb=sortCol==="hours"?b[nk]:(b[key]||"").toLowerCase();if(va<vb)return -1*sortDir;if(va>vb)return 1*sortDir;return 0;});
  },[filtered,sortCol,sortDir,isUtil,perStd]);

  const FB=({val,cur,set,color})=>(<button onClick={()=>set(val)} style={{padding:"4px 11px",borderRadius:6,fontSize:11,fontWeight:600,border:cur===val?"none":"1px solid #e2e8f0",cursor:"pointer",transition:"all .15s",background:cur===val?(color||"rgb(var(--brand-600))"):"#f8fafc",color:cur===val?"#fff":"#64748b"}}>{val==="ALL"?"All":val==="SAP Application Professional"?"Professional":val==="SAP Application Limited Pro"?"Limited Pro":val}</button>);
  const TB=({id,label})=>(<button onClick={()=>{setTab(id);setSelModule(null);}} style={{padding:"10px 18px",fontSize:12,fontWeight:700,border:"none",cursor:"pointer",background:"transparent",color:tab===id?"rgb(var(--brand-600))":"#94a3b8",borderBottom:tab===id?"2px solid rgb(var(--brand-600))":"2px solid transparent",marginBottom:-2}}>{label}</button>);

  const btnStyle={display:"flex",alignItems:"center",gap:6,padding:"9px 16px",borderRadius:8,border:"1px solid rgba(255,255,255,.4)",background:"rgba(255,255,255,.15)",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700};

  if (isLoading) return (
    <div style={{...S.page,display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:36,marginBottom:16}}>⏳</div>
        <div style={{fontSize:16,fontWeight:700,color:'#1e293b',marginBottom:8}}>{t('sapu.loading')}</div>
        <div style={{fontSize:12,color:'#94a3b8'}}>{t('sapu.title')}</div>
      </div>
    </div>
  );

  return(
    <div style={S.page}>
      {/* HEADER */}
      <div style={S.hdr}>
        <div>
          <div style={{fontSize:10,color:'#bfdbfe',letterSpacing:3,textTransform:'uppercase',marginBottom:3}}>{t('sapu.erpSystem')}</div>
          <div style={{fontSize:22,fontWeight:800,color:'#fff',letterSpacing:-0.5}}>{t('sapu.title')}</div>
          <div style={{fontSize:11,color:"#93c5fd",marginTop:2}}>{rawData.length} {t('sapu.records')} · <span style={{color:"#dbeafe"}}>{fileName}</span></div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          {[isUtil?{label:"Utilisation",val:overallUtil+"%"}:{label:t('sapu.totalHours'),val:fmtH(totalH)},{label:t('sapu.totalUsers'),val:totalU,c:'#fde68a'},{label:t('sapu.activeUsers'),val:activeU,c:'#6ee7b7'}].map(k=>(
            <div key={k.label} style={{background:"rgba(255,255,255,.18)",border:"1px solid rgba(255,255,255,.2)",borderRadius:10,padding:"9px 16px",textAlign:"center",minWidth:88}}>
              <div style={{fontSize:20,fontWeight:800,color:k.c||"#fff"}}>{k.val}</div>
              <div style={{fontSize:9,color:"#bfdbfe",marginTop:2,letterSpacing:1}}>{k.label.toUpperCase()}</div>
            </div>
          ))}
        </div>
      </div>

      {/* DB STATUS BAR */}
      {/* Info DB/source_file disembunyikan atas permintaan user (30 Okt 2026); hanya pesan status yang tetap tampil. */}
      {dbMsg&&(
        <div style={{background:"#f8fafc",borderBottom:"1px solid #e2e8f0",padding:"6px 24px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",fontSize:11}}>
          {dbMsg&&(
            <span style={{marginLeft:"auto",color:dbMsg.type==="ok"?"#16a34a":"#dc2626",fontWeight:600}}>
              {dbMsg.text}
            </span>
          )}
        </div>
      )}

      {/* FILTERS */}
      <div style={{...S.filters,flexDirection:"column",alignItems:"flex-start",gap:8,padding:"10px 24px"}}>
        {/* Baris 1: LANDSCAPE + COMPANY */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",width:"100%"}}>
          <span style={{fontSize:9,color:'#475569',letterSpacing:2,fontWeight:800,minWidth:80}}>LANDSCAPE</span>
          {landscapes.map(l=><FB key={l} val={l} cur={fLandscape} set={setFLandscape} color={l==="PROD"?"#16a34a":l==="DEV"?"#2563eb":"#d97706"}/>)}
          <div style={{width:1,height:18,background:"#e2e8f0",margin:"0 4px"}}/>
          <span style={{fontSize:9,color:'#475569',letterSpacing:2,fontWeight:800,minWidth:60}}>COMPANY</span>
          {relevantCompanies.map(c=><FB key={c} val={c} cur={fComp} set={setFComp} color={gc(c)}/>)}
        </div>
        {/* Baris 2: YEAR + LICENSE */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",width:"100%",paddingTop:4,borderTop:"1px dashed #f1f5f9"}}>
          <span style={{fontSize:9,color:'#475569',letterSpacing:2,fontWeight:800,minWidth:35}}>{t('sapu.year')}</span>
          {years.filter(y=>y!=="ALL").map(y=>{
            const active=fYears.includes(y);
            return(
              <button key={y}
                onClick={(e)=>{
                  if(e.ctrlKey||e.metaKey){
                    // Ctrl+click: toggle item di multiselect
                    setFYears(prev=>prev.includes(y)?prev.filter(x=>x!==y):[...prev,y]);
                    setFPer("ALL");
                  } else {
                    // Click biasa: toggle single (jika sudah active sendiri → ALL, jika beda → pilih satu)
                    setFYears(prev=>prev.length===1&&prev[0]===y?[]:[y]);
                    setFPer("ALL");
                  }
                }}
                title={active?t('sapu.ctrlDeselect'):t('sapu.ctrlMulti')}
                style={{padding:"4px 11px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",transition:"all .15s",
                  border:active?"none":"1px solid #e2e8f0",
                  background:active?"rgb(var(--brand-600))":"#f8fafc",
                  color:active?"#fff":"#64748b",
                  outline:active&&fYears.length>1?"2px solid #93c5fd":"none",
                  outlineOffset:1,
                }}>
                {y}
              </button>
            );
          })}
          <button onClick={()=>{setFYears([]);setFPer("ALL");}}
            style={{padding:"4px 11px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",transition:"all .15s",
              border:fYears.length===0?"none":"1px solid #e2e8f0",
              background:fYears.length===0?"rgb(var(--brand-600))":"#f8fafc",
              color:fYears.length===0?'#fff':'#64748b'}}>{t('sapu.all')}</button>
          {fYears.length>1&&<span style={{fontSize:10,color:"rgb(var(--brand-700))",background:"rgb(var(--brand-50))",padding:"2px 8px",borderRadius:4,fontWeight:600}}>
            {fYears.length} {t('sapu.yearsSelected')}
          </span>}
          <div style={{width:1,height:18,background:"#e2e8f0",margin:"0 4px"}}/>
          <span style={{fontSize:9,color:'#475569',letterSpacing:2,fontWeight:800,minWidth:52}}>LICENSE</span>
          {["ALL","SAP Application Professional","SAP Application Limited Pro"].map(l=><FB key={l} val={l} cur={fLic} set={setFLic}/>)}
        </div>
        {/* Baris 3: PERIOD (with multiselect CTRL support & smaller font) */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",width:"100%",paddingTop:4,borderTop:"1px dashed #f1f5f9"}}>
          <span style={{fontSize:9,color:'#475569',letterSpacing:2,fontWeight:800,minWidth:52}}>{t('sapu.period')}</span>
          {/* Toggle mode: Bulanan / Trimester (1/3 tahun) */}
          <button onClick={()=>{
              const next=fPerMode==="third"?"month":"third";
              setFPerMode(next);
              setPivotPeriodMode(next);
              setFPer("ALL");setFPers([]);
            }}
            title={fPerMode==="third"?"Show monthly period chips":"Group period chips per trimester (T1 Jan–Apr, T2 May–Aug, T3 Sep–Dec)"}
            style={{padding:"2px 8px",borderRadius:4,fontSize:10,fontWeight:700,cursor:"pointer",transition:"all .15s",
              border:"1px solid #16a34a",
              background:fPerMode==="third"?"#16a34a":"#fff",
              color:fPerMode==="third"?"#fff":"#16a34a"}}>
            {fPerMode==="third"?"📆 Monthly":"🗓 1/3 Year"}
          </button>
          {fPerMode==="third"&&(()=>{
            const months=periods.filter(p=>p!=="ALL"&&/^\d{4}-\d{2}$/.test(p));
            const thirds=[...new Set(months.map(periodThird))].sort();
            const monthsOf=(tk)=>months.filter(m=>periodThird(m)===tk);
            const RANGE={1:"Jan–Apr",2:"May–Aug",3:"Sep–Dec"};
            return thirds.map(tk=>{
              const ms=monthsOf(tk);
              const active=ms.length>0&&ms.every(m=>fPers.includes(m));
              return(
                <button key={tk}
                  onClick={(e)=>{
                    setFPer("ALL");
                    if(e.ctrlKey||e.metaKey){
                      // Ctrl+click: tambah/hapus trimester dari multiselect
                      setFPers(prev=>active?prev.filter(x=>!ms.includes(x)):[...new Set([...prev,...ms])]);
                    } else {
                      // Click biasa: pilih trimester ini saja (klik lagi → ALL)
                      const onlyThis=active&&fPers.length===ms.length;
                      setFPers(onlyThis?[]:ms);
                    }
                  }}
                  title={`${tk} (${RANGE[tk.slice(-1)]||""}): ${ms.join(", ")}`}
                  style={{padding:"2px 8px",borderRadius:4,fontSize:10,fontWeight:600,cursor:"pointer",transition:"all .15s",
                    border:active?"none":"1px solid #e2e8f0",
                    background:active?"rgb(var(--brand-600))":"#f8fafc",
                    color:active?"#fff":"#64748b"}}>
                  {tk} <span style={{opacity:.75,fontWeight:500}}>({RANGE[tk.slice(-1)]||""})</span>
                </button>
              );
            });
          })()}
          {fPerMode!=="third"&&periods.filter(p=>p!=="ALL").map(p=>{
            const active=fPers.includes(p)||fPer===p;
            return(
              <button key={p}
                onClick={(e)=>{
                  if(e.ctrlKey||e.metaKey){
                    // Ctrl+click: toggle multiselect
                    setFPers(prev=>prev.includes(p)?prev.filter(x=>x!==p):[...prev,p]);
                  } else {
                    // Click biasa: single select
                    setFPers([]);
                    setFPer(p);
                  }
                }}
                title={active?t('sapu.ctrlDeselect'):t('sapu.ctrlMulti')}
                style={{padding:"2px 8px",borderRadius:4,fontSize:10,fontWeight:600,cursor:"pointer",transition:"all .15s",
                  border:active?"none":"1px solid #e2e8f0",
                  background:active?"rgb(var(--brand-600))":"#f8fafc",
                  color:active?"#fff":"#64748b"}}>
                {p}
              </button>
            );
          })}
          <button onClick={()=>{setFPer("ALL");setFPers([]);}}
            style={{padding:"2px 8px",borderRadius:4,fontSize:10,fontWeight:600,cursor:"pointer",transition:"all .15s",
              border:fPer==="ALL"&&fPers.length===0?"none":"1px solid #e2e8f0",
              background:fPer==="ALL"&&fPers.length===0?"rgb(var(--brand-600))":"#f8fafc",
              color:fPer==="ALL"&&fPers.length===0?'#fff':'#64748b'}}>{t('sapu.all')}</button>
          {fPers.length>1&&<span style={{fontSize:9,color:"rgb(var(--brand-700))",background:"rgb(var(--brand-50))",padding:"2px 6px",borderRadius:3,fontWeight:600}}>
            {fPerMode==="third"?`${new Set(fPers.map(periodThird)).size} trimester · ${fPers.length}`:fPers.length} {t('sapu.selected')}
          </span>}
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}} title="Utilisation = monthly hours / standard (Mon-Fri x 8h) for the periods each user was active. Uncapped: shared or shift accounts can exceed 100%. See the Utilisation vs Standard tab for session-capped figures.">
            <span style={{fontSize:10,fontWeight:700,letterSpacing:2,color:"#64748b"}}>SHOW</span>
            <div style={{display:"flex",background:"#f1f5f9",borderRadius:6,padding:2,gap:2}}>
              {[["hours","Hours"],["util","Utilisation %"]].map(([k,l])=>(
                <button key={k} onClick={()=>setMetric(k)} style={{padding:"3px 10px",borderRadius:5,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,
                  background:metric===k?"rgb(var(--brand-600))":"transparent",color:metric===k?"#fff":"#64748b"}}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div style={S.tabs}>
        <TB id="overview" label={t('sapu.tab.overview')}/>
        <TB id="company"  label={t('sapu.tab.company')}/>
        <TB id="module"   label={t('sapu.tab.module')}/>
        <TB id="users"    label={t('sapu.tab.users')}/>
        <TB id="table"    label={t('sapu.tab.table')}/>
        <TB id="pivot"    label={t('sapu.tab.pivot')}/>
        <TB id="analytics" label={(lang==="id"||lang==="ID")?"Analitik":"Analytics"}/><TB id="anomali" label="Login Anomalies"/><TB id="capacity" label="Peak Concurrent"/><TB id="license" label="License Review"/><TB id="utilisation" label="Utilisation vs Standard"/>
      </div>

      <div style={S.content}>

            {['anomali','capacity','license','utilisation'].includes(tab) && (
              <div style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, fontSize: 12,
                background: USRUTL_CONN ? '#f1f5f9' : '#fef9c3', color: USRUTL_CONN ? '#475569' : '#a16207' }}>
                {USRUTL_CONN
                  ? <>Source: session log P01 <b>{fLandscape}</b> (connection {USRUTL_CONN})</>
                  : <>This tab reads one SAP system at a time - select landscape <b>ID</b>, <b>JB</b> or <b>KL</b>.</>}
              </div>
            )}

            {tab === 'utilisation' && USRUTL_CONN ? <UtilisationPanel api={api} connId={USRUTL_CONN} landscape={fLandscape} months={utMonths} company={fComp === 'ALL' ? '' : fComp} /> : null}

                    {/* Panel License Review - sap_um_users x sap_usr_utl_raw */}
            {tab === 'license' && (
              <>
                {licErr && <SectionError msg={licErr} />}
                {licLoading && <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Matching users against session log...</div>}
                {!licLoading && !licErr && lic && (() => {
                  const V = {
                    RECLAIM: ['Reclaim', '#fee2e2', '#b91c1c'],
                    DOWNGRADE: ['Downgrade', '#ffedd5', '#c2410c'],
                    REVIEW: ['Review', '#fef9c3', '#a16207'],
                  };
                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                        <div style={{ flex: 1, minWidth: 280, fontSize: 11.5, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', lineHeight: 1.6 }}>
                          <div style={{ fontWeight: 700, color: '#475569', marginBottom: 2 }}>How the Action column is decided</div>
                          <div>Scope: active users only (Valid Through is empty). Hours = total logon session time in the last {lic.days} days (sap_usr_utl_raw). Rules are checked in order; the first match wins:</div>
                          <div><b>1. Locked</b> - account is locked in SAP; not counted.</div>
                          <div><b>2. Reclaim</b> - no logon record at all (since 2023); license can be taken back.</div>
                          <div><b>3. Downgrade</b> - under {lic.thresholds.downgradeHours}h; candidate for a lower license (e.g. Professional to Limited Pro).</div>
                          <div><b>4. Review</b> - {lic.thresholds.downgradeHours}h to under {lic.thresholds.reviewHours}h; low usage, confirm with the user or manager.</div>
                          <div><b>5. Keep</b> - {lic.thresholds.reviewHours}h or more; active user, keep the license.</div>
                          <div style={{ marginTop: 2, color: '#94a3b8' }}>Notes: hours measure session duration, not transaction activity (idle open sessions still count). The current license type is not considered, so a Limited Pro user can still show Downgrade.</div>
                        </div>
                        <button onClick={exportLic} disabled={!lic.users.length}
                          style={{ padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                            border: '1px solid #0f766e', background: '#0f766e', color: '#fff',
                            cursor: lic.users.length ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>Export CSV</button>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
                        {[
                          { l: 'Reclaim', v: lic.summary.reclaim, t: '#b91c1c', s: 'never logged on' },
                          { l: 'Downgrade', v: lic.summary.downgrade, t: '#c2410c', s: 'under ' + lic.thresholds.downgradeHours + 'h' },
                          { l: 'Review', v: lic.summary.review, t: '#a16207', s: 'under ' + lic.thresholds.reviewHours + 'h' },
                          { l: 'Keep', v: lic.summary.keep, t: '#15803d', s: 'clearly active' },
                          { l: 'Locked', v: lic.summary.locked, t: '#64748b', s: 'not counted' },
                        ].map((c) => (
                          <div key={c.l} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 12px' }}>
                            <div style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>{c.l}</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: c.t }}>{fmt(c.v)}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.s}</div>
                          </div>
                        ))}
                      </div>

                      {(() => {
                        const lrows = lic.byLicense.filter((r) => !/test user/i.test(r.license || ''));
                        const showDeal = dealEdit || lrows.some((r) => r.deal != null);
                        const cols = ['License', ...(showDeal ? ['Deal'] : []), 'Total', 'Reclaim', 'Downgrade', 'Review', 'Keep', 'Locked'];
                        const sum = (k) => lrows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
                        const cell = (v, extra) => (
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...(extra || {}) }}>{v || '-'}</td>
                        );
                        const saveDeal = async (license, val) => {
                          try {
                            const r = await fetch(API_BASE + '/api/sap/usr-utl/license-deal', { method: 'PUT',
                              headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + getToken() },
                              body: JSON.stringify({ connectionId: USRUTL_CONN, license, qty: val }) });
                            if (!r.ok) throw new Error('HTTP ' + r.status);
                            setLic((cur) => cur && ({ ...cur, byLicense: cur.byLicense.map((x) => x.license === license
                              ? { ...x, deal: (val === '' || val == null) ? null : Math.max(0, parseInt(val, 10) || 0) } : x) }));
                          } catch (e) { alert('Failed to save deal: ' + e.message); }
                        };
                        return (<>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <div style={{ fontSize: 12, fontWeight: 700 }}>By license type</div>
                            <button onClick={() => setDealEdit((v) => !v)}
                              style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, border: '1px solid #cbd5e1',
                                background: dealEdit ? '#0f766e' : '#fff', color: dealEdit ? '#fff' : '#475569', cursor: 'pointer' }}>
                              {dealEdit ? 'Done' : 'Edit deal'}</button>
                          </div>
                          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 18 }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
                              <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                  {cols.map((h, i) => (
                                    <th key={h} style={{ padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', color: '#64748b',
                                      fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {lrows.map((r) => (
                                  <tr key={r.license} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '7px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.license || '(empty)'}</td>
                                    {showDeal && (dealEdit
                                      ? <td style={{ padding: '4px 10px', textAlign: 'right' }}>
                                          <input type="number" min="0" defaultValue={r.deal ?? ''} placeholder="-"
                                            onBlur={(e) => { const v = e.target.value.trim(); if (String(r.deal ?? '') !== v) saveDeal(r.license, v); }}
                                            style={{ width: 70, padding: '3px 6px', fontSize: 12, textAlign: 'right', border: '1px solid #cbd5e1', borderRadius: 6 }} />
                                        </td>
                                      : cell(r.deal, { fontWeight: 700 }))}
                                    {cell(r.total)}
                                    {cell(r.RECLAIM, r.RECLAIM > 0 ? { color: '#b91c1c', fontWeight: 700 } : undefined)}
                                    {cell(r.DOWNGRADE)}{cell(r.REVIEW)}{cell(r.KEEP)}{cell(r.LOCKED)}
                                  </tr>
                                ))}
                                <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                                  <td style={{ padding: '7px 10px' }}>Total license</td>
                                  {showDeal && cell(lrows.some((r) => r.deal != null) ? sum('deal') : null)}
                                  {cell(sum('total'))}{cell(sum('RECLAIM'), { color: '#b91c1c' })}
                                  {cell(sum('DOWNGRADE'))}{cell(sum('REVIEW'))}{cell(sum('KEEP'))}{cell(sum('LOCKED'))}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </>);
                      })()}

                      {lic.shared && lic.shared.length > 0 && (
                        <div style={{ marginBottom: 18 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#6d28d9' }}>Shared / shift accounts ({lic.shared.length})</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Logged on at several terminals at once or across shifts. A named-user license covers one person - check whether these need more licenses (or separate accounts per person).</div>
                          <div style={{ overflowX: 'auto', border: '1px solid #ddd6fe', borderRadius: 10 }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
                              <thead><tr style={{ background: '#f5f3ff' }}>
                                {['User', 'Name', 'Dept', 'License', 'Hours', 'Person-equivalent', 'Peak terminals', 'Avg terminals/day'].map((h, i) => (
                                  <th key={h} style={{ padding: '8px 10px', textAlign: i >= 4 ? 'right' : 'left', color: '#64748b', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #ddd6fe' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {lic.shared.map((u) => (
                                  <tr key={u.username} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{u.username}</td>
                                    <td style={{ padding: '7px 10px', color: '#475569', whiteSpace: 'nowrap' }}>{u.full_name || '-'}</td>
                                    <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{u.department || '-'}</td>
                                    <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{u.license}</td>
                                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{u.hours}</td>
                                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, color: '#6d28d9' }}>{u.person_eq}</td>
                                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{u.peak_terminals}</td>
                                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{u.avg_terminals}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Actionable accounts ({fmt(lic.users.length)})</div>
                      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              {['User', 'Name', 'Company', 'Dept', 'License', 'Last login', 'Hours', 'Util %', 'Active days', 'Action'].map((h, i) => (
                                <th key={h} style={{ padding: '8px 10px', textAlign: (i >= 6 && i <= 8) ? 'right' : 'left', color: '#64748b',
                                  fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {lic.users.length === 0 && (
                              <tr><td colSpan={10} style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8' }}>Nothing to act on - every unlocked account is in use.</td></tr>
                            )}
                            {lic.users.map((u) => (
                              <tr key={u.username} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{u.username}</td>
                                <td style={{ padding: '7px 10px', color: '#475569', whiteSpace: 'nowrap' }}>{u.full_name || '-'}</td>
                                <td style={{ padding: '7px 10px', color: '#64748b' }}>{u.company || '-'}</td>
                                <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{u.department || '-'}</td>
                                <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{u.license}</td>
                                <td style={{ padding: '7px 10px', color: u.last_login ? '#64748b' : '#b91c1c', fontWeight: u.last_login ? undefined : 700, whiteSpace: 'nowrap' }}>{u.last_login || 'NEVER'}</td>
                                <td style={{ padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{u.hours}</td>
                                <td style={{ padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{u.util_pct}%</td>
                                <td style={{ padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{u.active_days}</td>
                                <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                                  <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 999,
                                    background: V[u.verdict][1], color: V[u.verdict][2], fontSize: 10.5, fontWeight: 700 }}>{V[u.verdict][0]}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            {tab === 'capacity' && (
              <>
                {capErr && <SectionError msg={capErr} />}
                {capLoading && <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Computing concurrency...</div>}
                {!capLoading && !capErr && cap && (() => {
                  const pad2 = (h) => String(h).padStart(2, '0');
                  const maxH = Math.max.apply(null, cap.hourly.map((r) => r.avg_concurrent || 0)) || 1;
                  const maxD = Math.max.apply(null, cap.daily.map((r) => r.peak || 0)) || 1;
                  const inQuiet = (h) => {
                    for (let k = 0; k < 4; k++) if ((cap.summary.quiet_from + k) % 24 === h) return true;
                    return false;
                  };
                  const dowName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  return (
                    <>
                      <div style={{ fontSize: 11.5, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', lineHeight: 1.6, marginBottom: 12 }}>
                        <div style={{ fontWeight: 700, color: '#475569', marginBottom: 2 }}>How concurrency is calculated</div>
                        <div>Source: SAP logon sessions (sap_usr_utl_raw), last {cap.days} days. Each session is split by clock hour: a logon from 09:30 to 11:15 counts 0.5 at 09:00, 1.0 at 10:00 and 0.25 at 11:00. The pieces are summed and divided by the number of weekdays, so a value of 76.8 means about 77 users online throughout that hour on an average weekday.</div>
                        <div><b>Peak concurrent</b> - hour with the highest average, weekdays only (Mon-Fri); weekends follow plant shift patterns and would pull the average down.</div>
                        <div><b>Highest daily peak</b> - for each date in the last 30 days, the busiest hour of that day (all days, incl. weekends); shows busy days such as month-end closing.</div>
                        <div><b>Quietest window</b> - the 4 consecutive hours with the lowest total concurrency; a candidate slot for maintenance or downtime.</div>
                        <div><b>Logons</b> - new logons that started in that hour.</div>
                        <div style={{ marginTop: 2, color: '#94a3b8' }}>Notes: idle or hung sessions that stay open still count, so figures can be slightly higher than people actively working. Values are hourly averages; the momentary peak inside an hour can be higher.</div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
                        {[
                          { l: 'Peak concurrent', v: cap.summary.peak_concurrent, s: `at ${pad2(cap.summary.peak_hour)}:00, weekdays` },
                          { l: 'Highest daily peak', v: cap.summary.daily_peak_max, s: 'last 30 days' },
                          { l: 'Quietest window', v: `${pad2(cap.summary.quiet_from)}-${pad2(cap.summary.quiet_to)}`, s: `avg ${cap.summary.quiet_avg} concurrent` },
                          { l: 'Window', v: `${cap.days}d`, s: 'weekdays only' },
                        ].map((c) => (
                          <div key={c.l} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 12px' }}>
                            <div style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>{c.l}</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{c.v}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.s}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>Average concurrent sessions by hour</div>
                      <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 10 }}>
                        Weekdays only - weekend has its own shift pattern and would flatten the weekday peak. Green = quietest 4-hour block, safest maintenance window.
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 170, marginBottom: 18 }}>
                        {cap.hourly.map((r) => (
                          <div key={r.hour} title={`${pad2(r.hour)}:00 - ${r.avg_concurrent} concurrent, ${fmt(r.logons)} logons`}
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 3, height: '100%' }}>
                            <div style={{ fontSize: 9.5, color: '#c2410c', fontWeight: 700, height: 12 }}>{r.hour === cap.summary.peak_hour ? r.avg_concurrent : ''}</div>
                            <div style={{ width: '100%', height: `${Math.max(2, ((r.avg_concurrent || 0) / maxH) * 128)}px`,
                              background: r.hour === cap.summary.peak_hour ? '#c2410c' : (inQuiet(r.hour) ? '#4ade80' : '#93c5fd'),
                              borderRadius: '3px 3px 0 0' }} />
                            <div style={{ fontSize: 9.5, color: '#94a3b8', height: 12 }}>{r.hour % 3 === 0 ? pad2(r.hour) : ''}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>Daily peak, last 30 days</div>
                      <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 10 }}>Highest hour of each day, weekends included.</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 90, marginBottom: 18 }}>
                        {cap.daily.map((r) => (
                          <div key={r.date} title={`${r.date} - peak ${r.peak}`}
                            style={{ flex: 1, height: `${Math.max(2, ((r.peak || 0) / maxD) * 88)}px`, background: '#64748b', borderRadius: '2px 2px 0 0', opacity: .85 }} />
                        ))}
                      </div>

                      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              {['Day', 'Days in window', 'Sessions', 'Hours', 'Sessions/day'].map((h, i) => (
                                <th key={h} style={{ padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', color: '#64748b',
                                  fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {cap.byDow.map((r) => (
                              <tr key={r.dow} style={{ borderBottom: '1px solid #f1f5f9', background: (r.dow === 0 || r.dow === 6) ? '#fffbeb' : undefined }}>
                                <td style={{ padding: '7px 10px', fontWeight: 600 }}>{dowName[r.dow]}</td>
                                {[r.days, fmt(r.sessions), fmt(r.hours), r.days ? Math.round(r.sessions / r.days) : 0].map((v, i) => (
                                  <td key={i} style={{ padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{v}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
                {!capLoading && !capErr && cap && <CapacityUsers api={api} connId={USRUTL_CONN} days={cap.days} peakHour={cap.summary.peak_hour} />}
              </>
            )}

            {tab === 'anomali' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                  <span style={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', color: '#64748b' }}>Range</span>
                  {[30, 90, 180].map((d) => (
                    <button key={d} onClick={() => { setAnomDays(d); loadAnom(USRUTL_CONN, d); }}
                      style={{ padding: '4px 12px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                        border: `1px solid ${anomDays === d ? '#c2410c' : '#e2e8f0'}`,
                        background: anomDays === d ? '#fff7ed' : '#fff',
                        color: anomDays === d ? '#c2410c' : '#475569',
                        fontWeight: anomDays === d ? 700 : 500 }}>{d}d</button>
                  ))}
                  <button onClick={() => loadAnom(USRUTL_CONN, anomDays)} disabled={anomLoading}
                    style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 8, fontSize: 12,
                      border: '1px solid #e2e8f0', background: '#fff', color: '#475569',
                      cursor: anomLoading ? 'wait' : 'pointer' }}>Reload</button>
                </div>

                {anomErr && <SectionError msg={anomErr} />}
                {anomLoading && <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Calculating from session log...</div>}

                {!anomLoading && !anomErr && anom && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
                      {[
                        { l: 'Flagged users', v: fmt(anom.summary.flagged), t: anom.summary.flagged > 0 ? '#c2410c' : '#0f172a' },
                        { l: 'Hung sessions', v: fmt(anom.summary.hung_sessions), s: `${anom.summary.hung_pct}% of total hours` },
                        { l: 'Night logons 22:00-05:00', v: fmt(anom.summary.night_logons) },
                        { l: 'Weekend sessions', v: `${anom.summary.weekend_pct}%` },
                        { l: 'New terminals (30d)', v: fmt(anom.summary.new_terminals) },
                        { l: 'Total sessions', v: fmt(anom.summary.sessions), s: `${fmt(anom.summary.users)} users` },
                      ].map((c) => (
                        <div key={c.l} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 12px' }}>
                          <div style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>{c.l}</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: c.t || '#0f172a' }}>{c.v}</div>
                          {c.s && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.s}</div>}
                        </div>
                      ))}
                    </div>

                    <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 8 }}>
                      Flagged when: {anom.thresholds.hungMin}+ sessions longer than {anom.thresholds.hungHours}h (forgotten logoff), {anom.thresholds.nightMin}+ logons between 22:00-05:00, or {anom.thresholds.termMin}+ distinct terminals (shared account). Showing {fmt(anom.summary.listed)} of {fmt(anom.summary.flagged)}.
                    </div>

                    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            {['User', 'Dept', 'Flags', 'Sessions', 'Hours', 'Hung', 'Longest', 'Night', 'Weekend', 'Terminals', 'Last login'].map((h, i) => (
                              <th key={h} style={{ padding: '8px 10px', textAlign: i >= 3 && i <= 9 ? 'right' : 'left', color: '#64748b',
                                fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {anom.users.length === 0 && (
                            <tr><td colSpan={11} style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8' }}>No anomalies in this range.</td></tr>
                          )}
                          {anom.users.map((u) => (
                            <tr key={u.username + u.dept} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{u.username}</td>
                              <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{u.dept}</td>
                              <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                                {u.flags.map((f) => {
                                  const m = { hung: ['Hung', '#fee2e2', '#b91c1c'], night: ['Night', '#ffedd5', '#c2410c'], shared: ['Shared', '#e0e7ff', '#4338ca'] }[f];
                                  return <span key={f} style={{ display: 'inline-block', marginRight: 4, padding: '1px 7px', borderRadius: 999,
                                    background: m[1], color: m[2], fontSize: 10.5, fontWeight: 700 }}>{m[0]}</span>;
                                })}
                              </td>
                              {[fmt(u.sessions), fmt(u.hours), u.hung_sessions || '-', u.longest_hours + ' h', u.night_logons || '-', u.weekend_logons || '-', u.terminals].map((v, i) => (
                                <td key={i} style={{ padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{v}</td>
                              ))}
                              <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{u.last_login}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {anom.newTerminals.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>New terminals (first seen in the last 30 days)</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {anom.newTerminals.map((t2) => (
                            <span key={t2.terminal} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 9px', fontSize: 11.5, background: '#fff' }}>
                              <b style={{ fontFamily: 'monospace' }}>{t2.terminal}</b>
                              <span style={{ color: '#94a3b8' }}> - {t2.first_seen} - {t2.users} users, {fmt(t2.sessions)} sessions</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            {tab==="overview"&&(
          <div style={S.grid2}>
            <div style={S.card}>
              <div style={HEAD}><span>{t('sapu.hoursByCompany')}</span><span style={PILL}>{fmtH(totalH)} total</span></div>
              <HBarChart data={byComp} colorFor={(d)=>gc(d.name)} nameWidth={90} unit={isUtil?"pct":"h"}/>
            </div>
            <div style={S.card}>
              <div style={HEAD}><span>{t('sapu.hoursByModule')}</span><span style={PILL}>{byMod.length} modules</span></div>
              <DonutLegend data={byMod} colorFor={(d,i)=>CHART_MC[i%CHART_MC.length]} onPick={(d)=>{setSelModule(d.name);setTab("module");}}/>
            </div>
            <div style={{...S.card,gridColumn:"span 2"}}>
              <div style={HEAD}><span>{t('sapu.usageTrend')}</span><span style={PILL}>{byPeriod.length} periods</span></div>
              {byPeriodT.length?(
              <ResponsiveContainer width="100%" height={270}>
                <BarChart data={byPeriodT} margin={{top:20,right:8,left:0,bottom:0}} barCategoryGap="22%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7"/>
                  <XAxis dataKey="period" tick={{fill:"#64748b",fontSize:11}} {...AX}/>
                  <YAxis tick={{fill:"#94a3b8",fontSize:10}} tickFormatter={axK} width={44} {...AX}/>
                  <Tooltip {...TT} cursor={{fill:"rgba(148,163,184,.12)"}} formatter={(v,n)=>[`${(v||0).toLocaleString()} hrs`,n]}/>
                  <Legend iconType="square" iconSize={10} wrapperStyle={{fontSize:11,paddingTop:6}}/>
                  {compKeys.map((c,i)=>(
                    <Bar key={c} dataKey={c} stackId="a" fill={gc(c)} radius={i===compKeys.length-1?[4,4,0,0]:[0,0,0,0]}>
                      {i===compKeys.length-1&&<LabelList dataKey="_total" position="top" formatter={axK} style={{fontSize:10,fontWeight:700,fill:"#475569"}}/>}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
              ):<NoData/>}
            </div>
            <div style={S.card}>
              <div style={S.cardT}>{t('sapu.licenseTypeSplit')}</div>
              <DonutLegend data={byLic} size={180} colorFor={(d,i)=>[BRAND,"#0891b2","#7c3aed"][i%3]}/>
            </div>
            <div style={S.card}>
              <div style={HEAD}><span>{t('sapu.moduleRanking')}</span><span style={PILL}>Top {Math.min(8,byMod.length)}</span></div>
              {byMod.length?byMod.slice(0,8).map((m,i)=><BarRow key={m.name} label={m.name} val={m.val} max={byMod[0]?.val||1} color={CHART_MC[i%CHART_MC.length]}/>):<NoData/>}
            </div>
          </div>
        )}

        {tab==="company"&&(
          <div style={S.grid3}>
            {byComp.map(c=>{
              const cd=filtered.filter(d=>d.Company===c.name);
              const mods=agg(cd,"Module");
              const users=new Set(cd.map(d=>d.User)).size;
              const active=new Set(cd.filter(d=>d.TotalHours>0).map(d=>d.User)).size;
              return(<div key={c.name} style={{...S.card,border:`1px solid ${gc(c.name)}40`}}>
                <div style={{fontSize:16,fontWeight:800,color:gc(c.name),marginBottom:4}}>{c.name}</div>
                <div style={{fontSize:22,fontWeight:700,color:"#1e293b",marginBottom:4}}>{isUtil?`${c.val}%`:`${c.val.toLocaleString()} hrs`}</div>
                {isUtil&&<div style={{fontSize:11,color:"#64748b",marginBottom:2}}>{c.hours.toLocaleString()} of {c.std.toLocaleString()} std hrs</div>}
                <div style={{fontSize:11,color:"#94a3b8",marginBottom:12}}>{users} users · {active} active</div>
                {mods.map((m,i)=><BarRow key={m.name} label={m.name} val={m.val} max={mods[0]?.val||1} color={MC[i%MC.length]}/>)}
              </div>);
            })}
          </div>
        )}

        {tab==="module"&&(
          <div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:16}}>
              <span style={{fontSize:10,color:'#94a3b8',fontWeight:700,letterSpacing:2,textTransform:'uppercase',flexShrink:0}}>{t('sapu.module')}</span>
              <button onClick={()=>setSelModule(null)} style={{padding:"5px 14px",borderRadius:20,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,background:selModule===null?"rgb(var(--brand-600))":"#f1f5f9",color:selModule===null?"#fff":"#64748b"}}>{t('sapu.allModules')}</button>
              {byMod.map((m,i)=>(
                <button key={m.name} onClick={()=>setSelModule(selModule===m.name?null:m.name)}
                  style={{padding:"5px 14px",borderRadius:20,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,transition:"all .15s",
                    background:selModule===m.name?MC[i%MC.length]:"#f1f5f9",
                    color:selModule===m.name?"#fff":"#64748b",
                    boxShadow:selModule===m.name?`0 2px 8px ${MC[i%MC.length]}50`:"none"}}>
                  {m.name} <span style={{fontSize:10,opacity:.8}}>{m.val.toLocaleString()}h</span>
                </button>
              ))}
            </div>
            {!selModule&&(<>
              <div style={{...S.card,marginBottom:16}}>
                <div style={S.cardT}>{isUtil?"Utilisation per module (vs 5d x 8h) — click bar to drill-down":t('sapu.hoursPerModule')}</div>
                <HBarChart data={byModU} nameWidth={80} unit={isUtil?"pct":"h"} colorFor={(d,i)=>MC[i%MC.length]} onBarClick={(d)=>d&&setSelModule(d.name||(d.payload&&d.payload.name))}/>
              </div>
              <div style={S.grid3}>
                {byMod.map((mod,mi)=>{
                  const md=filtered.filter(d=>d.Module===mod.name);
                  const cos=agg(md,"Company");
                  return(<div key={mod.name} style={{...S.card,cursor:"pointer"}} onClick={()=>setSelModule(mod.name)}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:10,alignItems:"center"}}>
                      <span style={{fontSize:13,fontWeight:700,color:MC[mi%MC.length]}}>{mod.name}</span>
                      <span style={{fontSize:13,fontWeight:700,color:"#1e293b"}}>{mod.val.toLocaleString()} hrs</span>
                    </div>
                    {cos.map(co=><BarRow key={co.name} label={co.name} val={co.val} max={cos[0]?.val||1} color={gc(co.name)}/>)}
                    <div style={{marginTop:8,fontSize:10,color:'#94a3b8'}}>{t('sapu.clickToSeeUsers')}</div>
                  </div>);
                })}
              </div>
            </>)}
            {selModule&&(()=>{
              const modColor=MC[byMod.findIndex(m=>m.name===selModule)%MC.length]||"#2563eb";
              const modData=filtered.filter(d=>d.Module===selModule);
              const modTotal=Math.round(modData.reduce((s,d)=>s+d.TotalHours,0));
              const userMap={};modData.forEach(d=>{if(!userMap[d.User])userMap[d.User]={User:d.User,Company:d.Company,Dept:d.Dept,hours:0};userMap[d.User].hours+=d.TotalHours;});
              const userList=Object.values(userMap).sort((a,b)=>b.hours-a.hours);
              const maxH=userList[0]?.hours||1;
              return(<div>
                <div style={{...S.card,marginBottom:16,border:`1px solid ${modColor}40`,background:`linear-gradient(135deg,${modColor}08,#fff)`}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                    <div style={{width:12,height:12,borderRadius:3,background:modColor,flexShrink:0}}/>
                    <div style={{fontSize:20,fontWeight:800,color:modColor}}>{selModule}</div>
                    <button onClick={()=>setSelModule(null)} style={{marginLeft:"auto",padding:"5px 14px",borderRadius:8,border:"1px solid #e2e8f0",background:"#f8fafc",color:"#64748b",cursor:"pointer",fontSize:11,fontWeight:600}}>{t('sapu.back')}</button>
                  </div>
                  <div style={{display:"flex",gap:24,flexWrap:"wrap"}}>
                    {[[t('sapu.totalUsage'),`${modTotal.toLocaleString()} hrs`,'#1e293b'],["Users",userList.length,'#1e293b'],[t('sapu.activeWord'),userList.filter(u=>u.hours>0).length,'#16a34a'],[t('sapu.companies'),new Set(modData.map(d=>d.Company)).size,modColor]].map(([l,v,c])=>(
                      <div key={l}><div style={{fontSize:22,fontWeight:800,color:c}}>{v}</div><div style={{fontSize:10,color:"#94a3b8",letterSpacing:1}}>{l.toUpperCase()}</div></div>
                    ))}
                  </div>
                </div>
                <div style={{...S.card,marginBottom:16}}>
                  <div style={S.cardT}>{t('sapu.usersIn')} {selModule}</div>
                  <HBarChart data={userList} dataKey="hours" nameKey="User" nameWidth={150} color={modColor}/>
                </div>
                <div style={{...S.card,padding:0,overflow:"hidden"}}>
                  <div style={{padding:"14px 18px",borderBottom:"1px solid #e2e8f0",display:"flex",alignItems:"center"}}>
                    <div style={{...S.cardT,margin:0}}>{t('sapu.userDetail')} {selModule}</div>
                    <span style={{marginLeft:'auto',fontSize:11,color:'#94a3b8'}}>{userList.length} {t('sapu.usersWord')}</span>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead><tr style={{background:"#f8fafc",borderBottom:"2px solid #e2e8f0"}}>
                        {["#","User","Company","Dept","Total Hours","% of Max","Bar"].map(h=>(
                          <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:10,fontWeight:700,color:"#64748b",letterSpacing:1}}>{h.toUpperCase()}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {userList.map((u,i)=>{const pct=Math.round((u.hours/maxH)*100);return(
                          <tr key={u.User} style={{background:i%2?"#f8fafc":"#fff",borderBottom:"1px solid #f1f5f9"}}>
                            <td style={{padding:"8px 14px",color:"#94a3b8",fontSize:11}}>{i+1}</td>
                            <td style={{padding:"8px 14px",fontWeight:700,color:"#1e293b"}}>{u.User}</td>
                            <td style={{padding:"8px 14px"}}><span style={{background:`${gc(u.Company)}22`,color:gc(u.Company),padding:"2px 8px",borderRadius:5,fontSize:11,fontWeight:700}}>{u.Company}</span></td>
                            <td style={{padding:"8px 14px",color:"#64748b"}}>{u.Dept}</td>
                            <td style={{padding:"8px 14px",fontWeight:700,color:u.hours>200?"#dc2626":u.hours>100?"#d97706":"#16a34a"}}>{Math.round(u.hours).toLocaleString()} h</td>
                            <td style={{padding:"8px 14px",color:"#64748b",fontSize:11}}>{pct}%</td>
                            <td style={{padding:"8px 14px",minWidth:120}}>
                              <div style={{background:"#f1f5f9",borderRadius:3,height:10,overflow:"hidden"}}>
                                <div style={{width:`${pct}%`,height:"100%",background:gc(u.Company),borderRadius:3}}/>
                              </div>
                            </td>
                          </tr>
                        );})}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>);
            })()}
          </div>
        )}

        {tab==="users"&&(
          <div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,flexWrap:"wrap"}}>
              <div style={{display:"flex",background:"#f1f5f9",borderRadius:8,padding:3,gap:2}}>
                {[["highest","🔝 Highest Usage"],["lowest","🔻 Lowest Usage"]].map(([mode,label])=>(
                  <button key={mode} onClick={()=>setTopMode(mode)} style={{padding:"7px 16px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,
                    background:topMode===mode?(mode==="highest"?"rgb(var(--brand-600))":"#dc2626"):"transparent",
                    color:topMode===mode?"#fff":"#64748b",transition:"all .15s"}}>{label}</button>
                ))}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:11,color:'#94a3b8',fontWeight:600}}>{t('sapu.showTop')}</span>
                {[10,15,20,30].map(n=>(
                  <button key={n} onClick={()=>setTopN(n)} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #e2e8f0",cursor:"pointer",fontSize:11,fontWeight:700,
                    background:topN===n?"rgb(var(--brand-600))":"#f8fafc",color:topN===n?"#fff":"#64748b",transition:"all .15s"}}>{n}</button>
                ))}
                <span style={{fontSize:11,color:'#94a3b8',fontWeight:600}}>{t('sapu.usersWord')}</span>
              </div>
              <div style={{marginLeft:"auto",background:topMode==="highest"?"rgb(var(--brand-50))":"#fef2f2",border:`1px solid ${topMode==="highest"?"#93c5fd":"#fca5a5"}`,borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:600,color:topMode==="highest"?"#1d4ed8":"#dc2626"}}>
                {topMode==="highest"?'▲':'▼'} {topN} {t('sapu.ofUsers',{n:allUserHours.length})}
              </div>
            </div>
            <div style={{...S.card,marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <div style={S.cardT}>{`Top ${topN} — ${topMode==="highest"?t('sapu.highest'):t('sapu.lowest')}`}</div>
              </div>
              <HBarChart data={topUsers} dataKey={isUtil?"util":"hours"} unit={isUtil?"pct":"h"} nameKey="User" nameWidth={150} color={topMode==="highest"?BRAND:"#dc2626"} showPct={topMode==="highest"}/>
            </div>
            <div style={S.grid3}>
              {topUsers.map((u,i)=>{
                const ac=topMode==="highest"?gc(u.Company):"#dc2626";
                return(<div key={u.User} style={{...S.card,border:`1px solid ${ac}30`,position:"relative",overflow:"hidden"}}>
                  <div style={{position:"absolute",top:0,left:0,width:3,height:"100%",background:ac,borderRadius:"12px 0 0 12px"}}/>
                  <div style={{paddingLeft:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                      <span style={{fontSize:10,color:"#94a3b8",fontWeight:600}}>#{i+1}</span>
                      <span style={{fontSize:9,background:topMode==="highest"?"rgb(var(--brand-50))":"#fef2f2",color:topMode==="highest"?"rgb(var(--brand-600))":"#dc2626",padding:"1px 6px",borderRadius:4,fontWeight:700}}>{topMode==="highest"?`▲ ${t('sapu.highest').split(' ')[0]}`:"▼"+t('sapu.lowest').split(' ')[0]}</span>
                    </div>
                    <div style={{fontSize:12,fontWeight:700,color:"#1e293b",marginBottom:4,wordBreak:"break-all"}}>{u.User}</div>
                    <div style={{display:"flex",gap:5,marginBottom:8,flexWrap:"wrap"}}>
                      <span style={{fontSize:10,background:`${gc(u.Company)}22`,color:gc(u.Company),padding:"2px 7px",borderRadius:4,fontWeight:600}}>{u.Company}</span>
                      <span style={{fontSize:10,background:"#f1f5f9",color:"#64748b",padding:"2px 7px",borderRadius:4}}>{u.Module}</span>
                    </div>
                    <div style={{fontSize:18,fontWeight:800,color:ac}}>{isUtil?`${u.util}%`:`${Math.round(u.hours).toLocaleString()} hrs`}</div>
                    <div style={{fontSize:10,color:"#94a3b8"}}>{isUtil?`${Math.round(u.hours).toLocaleString()} of ${u.std.toLocaleString()} std hrs`:`${u.util}% of standard`}</div>
                    <div style={{marginTop:8,background:"#f1f5f9",borderRadius:3,height:4,overflow:"hidden"}}>
                      <div style={{width:`${isUtil?Math.min(100,u.util):Math.round((u.hours/(allUserHours[0]?.hours||1))*100)}%`,height:"100%",background:ac,borderRadius:3}}/>
                    </div>
                    <div style={{fontSize:9,color:"#94a3b8",marginTop:3}}>{isUtil?"of standard (5d x 8h)":<>{Math.round((u.hours/(allUserHours[0]?.hours||1))*100)}{t('sapu.ofMax')}</>}</div>
                  </div>
                </div>);
              })}
            </div>
          </div>
        )}

        {tab==="table"&&(
          <div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t('sapu.searchUserDept')}
                style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,color:"#1e293b",padding:"8px 14px",fontSize:12,width:220}}/>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {/* Tampilkan RELEVANT MODULES based on Landscape filter */}
                {relevantModules.map(m=><FB key={m} val={m} cur={fMod} set={setFMod}/>)}
              </div>
              <span style={{marginLeft:"auto",fontSize:11,color:"#64748b"}}>{tableData.length} {t('sapu.rows')}{tableData.length>100?" (showing 100)":""}</span>
            </div>
            <div style={{overflowX:"auto",background:"#fff",borderRadius:12,border:"1px solid #e2e8f0",boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead><tr>
                  {[["user","User"],["company","Company"],["dept","Dept"],["module","Module"],["license","License"],["lic","Lic"],["hours",isUtil?"Utilisation":"Total Hours"]].map(([col,label])=>(
                    <SortTh key={col} col={col} label={label} sortCol={sortCol} sortDir={sortDir} onSort={handleSort}/>
                  ))}
                </tr></thead>
                <tbody>
                  {tableData.slice(0,100).map((r,i)=>{
                    const isExpanded=expandedUser===r.User;
                    // Get all periods for this user from rawData, respecting active filters
                    let userPeriods=Array.from(new Set(rawData.filter(d=>d.User===r.User&&(fLandscape==="ALL"||d.Landscape===fLandscape)&&(fComp==="ALL"||d.Company===fComp)&&(fMod==="ALL"||d.Module===fMod)&&(fYears.length===0||fYears.includes((d.Period||"").substring(0,4)))).map(d=>d.Period))).sort();
                    // Filter by active PERIOD selections (multiselect or single)
                    if(fPers.length>0) userPeriods=userPeriods.filter(p=>fPers.includes(p));
                    else if(fPer!=="ALL") userPeriods=userPeriods.filter(p=>p===fPer);
                    return(
                      <React.Fragment key={i}>
                        <tr 
                          onClick={()=>setExpandedUser(isExpanded?null:r.User)}
                          style={{background:i%2?"#f8fafc":"#fff",borderBottom:"1px solid #f1f5f9",cursor:"pointer",transition:"background .15s"}}
                          onMouseEnter={(e)=>e.target.parentElement.style.background="#f0f4f8"}
                          onMouseLeave={(e)=>e.target.parentElement.style.background=i%2?"#f8fafc":"#fff"}>
                          <td style={{padding:"8px 12px",fontWeight:600,color:"#1e293b"}}><span style={{marginRight:6}}>{isExpanded?"▼":"▶"}</span>{r.User}</td>
                          <td style={{padding:"8px 12px"}}><span style={{background:`${gc(r.Company)}22`,color:gc(r.Company),padding:"2px 8px",borderRadius:5,fontSize:11,fontWeight:700}}>{r.Company}</span></td>
                          <td style={{padding:"8px 12px",color:"#64748b"}}>{r.Dept}</td>
                          <td style={{padding:"8px 12px",color:"#475569"}}>{r.Module}</td>
                          <td style={{padding:"8px 12px",color:"#64748b",fontSize:11}}>{r.LicenseType.replace("SAP Application ","")}</td>
                          <td style={{padding:"8px 12px"}}><span style={{background:r.Lic==="CB"?"#dbeafe":"#fef3c7",color:r.Lic==="CB"?"#1d4ed8":"#92400e",padding:"2px 8px",borderRadius:5,fontSize:11,fontWeight:700}}>{r.Lic}</span></td>
                          <td title={`${Math.round(r.hours).toLocaleString()} h of ${r.std.toLocaleString()} std h`} style={{padding:"8px 12px",fontWeight:700,color:isUtil?(r.util>100?"#d97706":r.util>=60?"#16a34a":r.util>=25?"#0369a1":"#dc2626"):(r.hours>200?"#dc2626":r.hours>100?"#d97706":"#16a34a")}}>{isUtil?`${r.util}%`:`${Math.round(r.hours).toLocaleString()} h`}</td>
                        </tr>
                        {isExpanded&&userPeriods.length>0&&userPeriods.map((p,pi)=>{
                          // Filter periodData respecting ALL active filters including YEAR and PERIOD selections
                          const periodData=rawData.filter(d=>
                            d.User===r.User&&
                            d.Period===p&&
                            (fLandscape==="ALL"||d.Landscape===fLandscape)&&
                            (fComp==="ALL"||d.Company===fComp)&&
                            (fMod==="ALL"||d.Module===fMod)&&
                            (fYears.length===0||fYears.includes((d.Period||"").substring(0,4)))&&
                            (fPers.length===0&&fPer==="ALL"||(fPers.length>0&&fPers.includes(d.Period))||(fPer!=="ALL"&&d.Period===fPer))
                          );
                          const pHours=periodData.reduce((sum,d)=>sum+((d.TotalHours||0)*1),0); // Use TotalHours, ensure numeric
                          const pLicenses=Array.from(new Set(periodData.map(d=>d.Lic)));
                          return(
                            <tr key={`${i}-${pi}`} style={{background:"#f9fafb",borderBottom:"1px solid #f1f5f9",borderLeft:"4px solid rgb(var(--brand-600))"}}>
                              <td style={{padding:"6px 12px 6px 32px",color:"#64748b",fontSize:11}}><span style={{color:"#94a3b8"}}>└ {p}</span></td>
                              <td style={{padding:"6px 12px",fontSize:11,color:"#64748b"}}>{periodData.length} record{periodData.length>1?"s":""}</td>
                              <td colSpan="4" style={{padding:'6px 12px',fontSize:11,color:'#64748b'}}>{t('sapu.licenses')} {pLicenses.join(", ")}</td>
                              <td style={{padding:"6px 12px",fontWeight:600,fontSize:11,color:pHours>0?"rgb(var(--brand-600))":"#94a3b8"}}>{Math.round(pHours).toLocaleString()} h</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {tableData.length>100&&(
                <div style={{textAlign:"center",padding:14,color:"#94a3b8",fontSize:11,background:"#f8fafc",borderTop:"1px solid #e2e8f0"}}>
                  {t('sapu.showingLimit',{n:tableData.length})}
                </div>
              )}
            </div>
          </div>
        )}

        {/* PIVOT TABLE TAB */}
        {tab==="pivot"&&(
          <div style={{padding:"20px",background:"#f8fafc"}}>
            {/* Module Filter + Search */}
            <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:16,flexWrap:"wrap"}}>
              {/* Module Filter Buttons */}
              <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                <span style={{fontSize:11,fontWeight:700,color:'#475569',marginRight:4}}>{t('sapu.moduleLabel')}</span>
                {["ALL",...Array.from(new Set(filtered.map(d=>d.Module))).sort()].map(m=>(
                  <button 
                    key={m}
                    onClick={()=>setPivotModuleFilter(m)}
                    style={{
                      padding:"5px 12px",
                      borderRadius:4,
                      border:"1px solid #cbd5e1",
                      background:pivotModuleFilter===m?"#3b82f6":"#fff",
                      color:pivotModuleFilter===m?"#fff":"#475569",
                      cursor:"pointer",
                      fontSize:11,
                      fontWeight:pivotModuleFilter===m?600:500,
                      transition:"all 0.2s"
                    }}
                  >
                    {m}
                  </button>
                ))}
              </div>
              
              {/* Search Input */}
              <input
                type="text"
                placeholder={t('sapu.searchUser')}
                value={pivotSearch}
                onChange={(e)=>setPivotSearch(e.target.value.toUpperCase())}
                style={{
                  padding:"6px 10px",
                  borderRadius:4,
                  border:"1px solid #cbd5e1",
                  fontSize:11,
                  width:"150px",
                  outline:"none"
                }}
              />

              {/* Unit Toggle: Hours / Days (8h = 1 day) */}
              <button
                onClick={()=>setPivotUnit(u=>u==="hours"?"days":"hours")}
                title="Asumsi 1 hari = 8 jam"
                style={{
                  marginLeft:"auto",
                  padding:"6px 14px",
                  borderRadius:4,
                  border:"1px solid rgb(var(--brand-600))",
                  background:pivotUnit==="days"?"rgb(var(--brand-600))":"#fff",
                  color:pivotUnit==="days"?"#fff":"rgb(var(--brand-600))",
                  cursor:"pointer",
                  fontSize:11,
                  fontWeight:600,
                  transition:"all 0.2s",
                  whiteSpace:"nowrap"
                }}
              >
                {pivotUnit==="days"?"⏱ Show Hours":"📅 Convert to Day"}
              </button>

              {/* Period Toggle: Monthly / Trimester 1/3-year (T1/T2/T3) */}
              <button
                onClick={()=>setPivotPeriodMode(m=>m==="month"?"third":"month")}
                title="1/3 tahun = 4 bulan. T1: Jan-Apr, T2: Mei-Agu, T3: Sep-Des"
                style={{
                  padding:"6px 14px",
                  borderRadius:4,
                  border:"1px solid #16a34a",
                  background:pivotPeriodMode==="third"?"#16a34a":"#fff",
                  color:pivotPeriodMode==="third"?"#fff":"#16a34a",
                  cursor:"pointer",
                  fontSize:11,
                  fontWeight:600,
                  transition:"all 0.2s",
                  whiteSpace:"nowrap"
                }}
              >
                {pivotPeriodMode==="third"?"📆 Show Monthly":"🗓 To 1/3 Year"}
              </button>
              {pivotPeriodMode==="third"&&(
                <span style={{fontSize:10,color:"#64748b",fontWeight:500}}>(1/3 tahun = 4 bln · T1:Jan-Apr T2:Mei-Agu T3:Sep-Des)</span>
              )}
              {pivotUnit==="days"&&(
                <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10,color:"#64748b",fontWeight:500}}>
                  (
                  <input
                    type="number"
                    min="1"
                    max="24"
                    step="0.5"
                    value={pivotHoursPerDay}
                    onChange={(e)=>{
                      const v=parseFloat(e.target.value);
                      setPivotHoursPerDay(Number.isFinite(v)&&v>0?v:1);
                    }}
                    style={{
                      width:48,
                      padding:"3px 6px",
                      borderRadius:4,
                      border:"1px solid #cbd5e1",
                      fontSize:11,
                      textAlign:"right",
                      outline:"none"
                    }}
                  />
                  jam = 1 hari)
                </span>
              )}
            </div>
            
            <div style={{overflowX:"auto",background:"#fff",borderRadius:8,border:"1px solid #e2e8f0"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                <thead>
                  <tr style={{background:"#f1f5f9",borderBottom:"2px solid #cbd5e1"}}>
                    <th onClick={()=>onPivotSort("user")} style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:pivotSort.col==="user"?"rgb(var(--brand-600))":"#475569",minWidth:80,cursor:"pointer",userSelect:"none"}}>User{pivotArrow("user")}</th>
                    <th onClick={()=>onPivotSort("module")} style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:pivotSort.col==="module"?"rgb(var(--brand-600))":"#475569",minWidth:60,cursor:"pointer",userSelect:"none"}}>Module{pivotArrow("module")}</th>
                    {(pivotPeriodMode==="third"?thirdCols:periodCols).map(c=>(
                      <th key={c} onClick={()=>onPivotSort(c)} style={{padding:"10px 8px",textAlign:"right",fontWeight:700,color:pivotSort.col===c?"rgb(var(--brand-600))":"#475569",minWidth:60,cursor:"pointer",userSelect:"none",background:c===(pivotPeriodMode==="third"?periodThird(fPer):fPer)?"#dbeafe":"transparent"}}>{c}{pivotArrow(c)}</th>
                    ))}
                    <th onClick={()=>onPivotSort("total")} style={{padding:"10px 12px",textAlign:"right",fontWeight:700,color:pivotSort.col==="total"?"rgb(var(--brand-600))":"#1e293b",minWidth:60,cursor:"pointer",userSelect:"none",background:"#e0f2fe"}}>Total{pivotArrow("total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(()=>{
                    const cols=pivotPeriodMode==="third"?thirdCols:periodCols;
                    const users=Array.from(new Set(filtered.map(d=>d.User)))
                      .filter(user=>!pivotSearch||user.toUpperCase().includes(pivotSearch));
                    // Bangun baris (user × module) beserta nilai per kolom + total
                    const rows=[];
                    users.forEach(user=>{
                      const userRows=filtered.filter(d=>d.User===user);
                      Array.from(new Set(userRows.map(d=>d.Module))).sort()
                        .filter(m=>pivotModuleFilter==="ALL"||m===pivotModuleFilter)
                        .forEach(mod=>{
                          const modData=userRows.filter(d=>d.Module===mod);
                          const stdOf=(arr)=>Array.from(new Set(arr.map(d=>d.Period))).reduce((a,p)=>a+perStd(p),0);
                          const pctOf=(h,arr)=>{const st=stdOf(arr);return st?Math.round(h/st*1000)/10:0;};
                          const totH=modData.reduce((s,d)=>s+d.TotalHours,0);
                          const total=isUtil?pctOf(totH,modData):totH;
                          const byCol=cols.map(c=>{
                            const cData=pivotPeriodMode==="third"
                              ? modData.filter(d=>periodThird(d.Period)===c)
                              : modData.filter(d=>d.Period===c);
                            const h=cData.reduce((s,d)=>s+d.TotalHours,0);
                            return isUtil?(h>0?pctOf(h,cData):0):h;
                          });
                          rows.push({user,mod,byCol,total});
                        });
                    });
                    // Urutkan sesuai kolom terpilih
                    const {col,dir}=pivotSort;
                    rows.sort((a,b)=>{
                      let cmp;
                      if(col==="user") cmp=a.user.localeCompare(b.user);
                      else if(col==="module") cmp=a.mod.localeCompare(b.mod);
                      else if(col==="total") cmp=a.total-b.total;
                      else { const i=cols.indexOf(col); cmp=(a.byCol[i]||0)-(b.byCol[i]||0); }
                      if(cmp!==0) return cmp*dir;
                      return a.user.localeCompare(b.user)||a.mod.localeCompare(b.mod);
                    });
                    let prevUser=null;
                    return rows.map((r,ri)=>{
                      const showUser=r.user!==prevUser; prevUser=r.user;
                      return(
                        <tr key={`${r.user}-${r.mod}`} style={{borderBottom:"1px solid #e2e8f0",background:ri%2?"#f9fafb":"#fff"}}>
                          <td style={{padding:"8px 12px",color:"#1e293b",fontWeight:600}}>{showUser?r.user:""}</td>
                          <td style={{padding:"8px 12px",color:"#64748b"}}>{r.mod}</td>
                          {r.byCol.map((hrs,pi)=>(
                            <td key={pi} style={{padding:"8px 8px",textAlign:"right",color:"#475569",background:cols[pi]===(pivotPeriodMode==="third"?periodThird(fPer):fPer)?"#dbeafe":"transparent"}}>
                              {hrs>0?(isUtil?`${hrs}%`:pivotUnit==="days"?fmtDays(hrs,pivotHoursPerDay):fmtHMS(hrs)):"-"}
                            </td>
                          ))}
                          <td style={{padding:"8px 12px",textAlign:"right",fontWeight:700,color:"rgb(var(--brand-600))",background:"#e0f2fe"}}>{isUtil?`${r.total}%`:pivotUnit==="days"?fmtDays(r.total,pivotHoursPerDay):fmtHMS(r.total)}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab==="analytics"&&(
          <AnalyticsTab rows={anBase} lang={lang}/>
        )}

      </div>

    </div>
  );
}


// Daftar user yang online pada jam tertentu (hari kerja) - tab Peak Concurrent
function CapacityUsers({ api, connId, days, peakHour }) {
  const [hour, setHour] = useState(peakHour || 0);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(100);
  useEffect(() => { setHour(peakHour || 0); }, [peakHour]);
  useEffect(() => {
    if (!connId) return;
    let alive = true;
    setLoading(true); setErr('');
    api('/api/sap/usr-utl/capacity-users?connectionId=' + encodeURIComponent(connId) + '&days=' + (days || 90) + '&hour=' + hour)
      .then((d) => { if (alive) { setData(d); setLimit(100); } })
      .catch((e) => { if (alive) { setData(null); setErr(e.message || String(e)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, connId, days, hour]);
  const pad2 = (h) => String(h).padStart(2, '0');
  const ql = q.trim().toLowerCase();
  const rows = ((data && data.users) || []).filter((u) => !ql || [u.username, u.full_name, u.company, u.department].join(' ').toLowerCase().includes(ql));
  const exportCsv = () => {
    const head = ['User', 'Name', 'Company', 'Dept', 'Avg concurrent share', 'Hours in slot', 'Days present', 'Presence %', 'Sessions', 'Last seen'];
    const esc = (v) => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const lines = [head.map(esc).join(',')].concat(rows.map((u) => [u.username, u.full_name, u.company, u.department, u.avg_share, u.hours_in_slot, u.days_present, u.presence_pct, u.sessions, u.last_seen].map(esc).join(',')));
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = 'concurrent_users_' + pad2(hour) + '00.csv'; a.click();
  };
  const th = { padding: '8px 10px', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td = { padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 12.5 };
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#334155' }}>
          Concurrent users at {pad2(hour)}:00-{pad2((hour + 1) % 24)}:00 {data ? '(' + data.total + ')' : ''}
        </div>
        <select value={hour} onChange={(e) => setHour(Number(e.target.value))}
          style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}>
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{pad2(h)}:00{h === peakHour ? ' (peak)' : ''}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user, name, company..."
          style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12, minWidth: 200 }} />
        <button onClick={exportCsv} disabled={!rows.length}
          style={{ marginLeft: 'auto', padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: '#0f766e', color: '#fff', border: 'none', cursor: 'pointer' }}>Export CSV</button>
      </div>
      <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 8 }}>
        Users with a session overlapping this hour on weekdays in the last {days} days{data ? ' (' + data.workdays + ' weekdays)' : ''}. Avg share = hours in this slot / weekdays; the shares of all users add up to the concurrency value for this hour. Presence % = share of weekdays the user was online in this hour.
      </div>
      {err && <div style={{ padding: 12, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: 13 }}>{err}</div>}
      {loading && <div style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading users...</div>}
      {!loading && !err && data && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['User', 'Name', 'Company', 'Dept', 'Avg share', 'Hours in slot', 'Days present', 'Presence %', 'Sessions', 'Last seen'].map((h, i) => (
                <th key={h} style={Object.assign({}, th, { textAlign: i < 4 ? 'left' : 'right' })}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.slice(0, limit).map((u) => (
                <tr key={u.username}>
                  <td style={Object.assign({}, td, { fontWeight: 600 })}>{u.username}</td>
                  <td style={td}>{u.full_name || '-'}</td>
                  <td style={td}>{u.company || '-'}</td>
                  <td style={td}>{u.department || '-'}</td>
                  <td style={Object.assign({}, td, { textAlign: 'right', fontWeight: 600 })}>{u.avg_share}</td>
                  <td style={Object.assign({}, td, { textAlign: 'right' })}>{u.hours_in_slot}</td>
                  <td style={Object.assign({}, td, { textAlign: 'right' })}>{u.days_present}</td>
                  <td style={Object.assign({}, td, { textAlign: 'right' })}>{u.presence_pct}%</td>
                  <td style={Object.assign({}, td, { textAlign: 'right' })}>{u.sessions}</td>
                  <td style={Object.assign({}, td, { textAlign: 'right' })}>{u.last_seen}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={10} style={Object.assign({}, td, { textAlign: 'center', color: '#94a3b8' })}>No users in this hour.</td></tr>}
            </tbody>
          </table>
          {rows.length > limit && (
            <button onClick={() => setLimit(limit + 200)} style={{ marginTop: 8, padding: '5px 14px', borderRadius: 8, fontSize: 12, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
              Show more ({rows.length - limit} left)
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// -- Utilisation vs Standard ---------------------------------------------------
// Jam login nyata (per hari dibatasi 8 jam) dibanding jam kerja standar
// 5 hari x 8 jam. Hari libur dideteksi otomatis di backend dari data login.
const UT_BAND = {
  LOW: ['Low (<25%)', '#fee2e2', '#b91c1c'],
  NORMAL: ['Normal (25-60%)', '#e0f2fe', '#0369a1'],
  HIGH: ['High (60-100%)', '#dcfce7', '#15803d'],
  OVER: ['Over (>100%)', '#fef3c7', '#b45309'],
  SHARED: ['Shared/Shift', '#ede9fe', '#6d28d9'],
};
function UtilisationPanel({ api, connId, landscape, months = [], company = '' }) {
  const [days, setDays] = useState(90);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('department');
  const [bandF, setBandF] = useState('ALL');
  const [q, setQ] = useState('');
  const [gSort, setGSort] = useState({ k: 'hours', d: -1 });
  const [uSort, setUSort] = useState({ k: 'util_pct', d: -1 });
  const monthsKey = months.join(',');
  useEffect(() => {
    let alive = true;
    setLoading(true); setErr('');
    api('/api/sap/usr-utl/utilisation?connectionId=' + encodeURIComponent(connId) + '&days=' + days + '&capHours=8'
      + (monthsKey ? '&months=' + encodeURIComponent(monthsKey) : '') + (company ? '&company=' + encodeURIComponent(company) : ''))
      .then((x) => { if (alive) setD(x); })
      .catch((e) => { if (alive) { setD(null); setErr(e.message || String(e)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, connId, days, monthsKey, company]);

  const sortBy = (arr, s) => [...arr].sort((a, b) => {
    const x = a[s.k], y = b[s.k];
    if (typeof x === 'number' || typeof y === 'number') return ((Number(x) || 0) - (Number(y) || 0)) * s.d;
    return String(x || '').localeCompare(String(y || '')) * s.d;
  });
  const clickSort = (set) => (k, num) => set((s) => s.k === k ? { k, d: -s.d } : { k, d: num ? -1 : 1 });
  const arrow = (s, k) => s.k === k ? (s.d > 0 ? ' \u25b2' : ' \u25bc') : ' \u21c5';

  const users = useMemo(() => sortBy((d ? d.users : []).filter((u) =>
    (bandF === 'ALL' || u.band === bandF) &&
    (!q || (u.username + ' ' + u.full_name + ' ' + u.department + ' ' + u.license).toLowerCase().includes(q.toLowerCase()))), uSort), [d, bandF, q, uSort]);

  const exportCsv = () => {
    if (!d) return;
    const esc = (v) => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const head = ['Username', 'Full name', 'Company', 'Department', 'License', 'Hours (session cap ' + (d.sessionCapHours || 10) + 'h)', 'Raw hours', 'Standard hours', 'Util %', 'Person-equivalent', 'Band', 'Active days', 'Peak terminals', 'Avg terminals/day', 'Night-shift days', 'Capped sessions'];
    const body = users.map((u) => [u.username, u.full_name, u.company, u.department, u.license, u.hours, u.raw_hours, u.standard_hours, u.util_pct, u.person_eq, u.band, u.active_days, u.peak_terminals, u.avg_terminals, u.night_days, u.capped_sessions].map(esc).join(','));
    const blob = new Blob(['\ufeff' + [head.join(','), ...body].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'sap-utilisation-' + landscape + '-' + days + 'd-' + new Date().toISOString().slice(0, 10) + '.csv';
    a.click(); URL.revokeObjectURL(a.href);
  };

  const th = { padding: '8px 10px', color: '#64748b', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0', textAlign: 'right' };
  const td = { padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const bar = (p) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
      <div style={{ width: 90, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: Math.min(100, p) + '%', height: '100%', background: p > 100 ? '#d97706' : p >= 60 ? '#16a34a' : p >= 25 ? '#0284c7' : '#dc2626' }} />
      </div>
      <span style={{ minWidth: 44 }}>{p}%</span>
    </div>
  );
  const btn = (on) => ({ padding: '3px 10px', borderRadius: 6, fontSize: 11.5, border: '1px solid ' + (on ? '#0f766e' : '#cbd5e1'), background: on ? '#0f766e' : '#fff', color: on ? '#fff' : '#475569', cursor: 'pointer' });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {monthsKey ? (
          <span style={{ fontSize: 12, color: '#475569' }}>
            Following page filters: <b>{months.length === 1 ? months[0] : months[0] + ' .. ' + months[months.length - 1] + ' (' + months.length + ' months)'}</b>
            {company ? <> · company <b>{company}</b></> : null}
            {d && d.from ? <span style={{ color: '#94a3b8' }}> · data used {d.from} to {d.to}</span> : null}
          </span>
        ) : (<>
          <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Range</span>
          {[30, 90, 180].map((x) => <button key={x} onClick={() => setDays(x)} style={btn(days === x)}>{x}d</button>)}
          <span style={{ fontSize: 11, color: '#94a3b8' }}>(no YEAR/PERIOD filter set){company ? <> · company <b>{company}</b></> : null}</span>
        </>)}
        <div style={{ flex: 1 }} />
        <button onClick={exportCsv} disabled={!d || !users.length} style={{ ...btn(true), padding: '5px 14px', fontWeight: 600 }}>Export CSV</button>
      </div>
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 11.5, color: '#64748b', marginBottom: 14, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, color: '#475569' }}>How utilisation is calculated</div>
        <div><b>Actual hours</b> = logon session time; each single session is capped at {d ? d.sessionCapHours : 10}h (one shift + overtime) so forgotten logoffs do not inflate it, while a PC shared across 3 shifts still counts in full. Weekend hours still count.</div>
        <div><b>Shared/Shift</b> = account logged on at 2+ terminals at the same time, or used on night shift (22:00-06:00) / for more than one shift a day on 5+ days. These accounts are measured as <b>person-equivalent</b> (hours / standard of one person), not as one person's %.</div>
        <div><b>Standard hours</b> = working days (Mon-Fri) x 8h, from the start of the range (or the user's first logon if later) to yesterday.</div>
        <div><b>Holidays</b> are detected automatically: a weekday with fewer than 30% of the usual (median) number of active users is treated as a holiday and excluded{d && d.holidays && d.holidays.length ? <> - {d.holidays.length} detected: {d.holidays.join(', ')}</> : null}.</div>
        <div style={{ color: '#94a3b8' }}>Session time measures SAP presence, not transaction activity. KL sessions without a logoff record count as 0h, so KL may read lower.</div>
      </div>
      {err && <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fee2e2', color: '#b91c1c', fontSize: 12, marginBottom: 12 }}>{err}</div>}
      {loading && <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Calculating utilisation...</div>}
      {!loading && !err && d && !d.summary && <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No session data in this range.</div>}
      {!loading && !err && d && d.summary && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
            {[
              { l: 'Overall utilisation', v: d.summary.util_pct + '%', s: d.summary.hours.toLocaleString() + 'h of ' + d.summary.standard_hours.toLocaleString() + 'h', t: '#0f766e' },
              { l: 'Full-time equivalent', v: d.summary.fte, s: d.summary.users + ' users active', t: '#334155' },
              ...['LOW', 'NORMAL', 'HIGH', 'OVER', 'SHARED'].map((b) => ({ l: UT_BAND[b][0], v: d.summary[b], s: 'users', t: UT_BAND[b][2], b })),
            ].map((c) => (
              <div key={c.l} onClick={c.b ? () => setBandF(bandF === c.b ? 'ALL' : c.b) : undefined}
                style={{ background: c.b && bandF === c.b ? UT_BAND[c.b][1] : '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 12px', cursor: c.b ? 'pointer' : 'default' }}>
                <div style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>{c.l}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: c.t }}>{c.v}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.s}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginRight: 6 }}>By</div>
            <button onClick={() => setView('department')} style={btn(view === 'department')}>Department</button>
            <button onClick={() => setView('license')} style={btn(view === 'license')}>License</button>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 18 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
              <thead><tr style={{ background: '#f8fafc' }}>
                {[[view === 'license' ? 'License' : 'Department', 'key'], ['Users', 'users'], ['Hours', 'hours'], ['Standard', 'standard_hours'], ['Utilisation', 'util_pct'], ['Low', 'LOW'], ['Normal', 'NORMAL'], ['High', 'HIGH'], ['Over', 'OVER'], ['Shared', 'SHARED']].map(([h, k], i) =>
                  <th key={k} onClick={() => clickSort(setGSort)(k, i > 0)} style={{ ...th, textAlign: i === 0 ? 'left' : 'right', cursor: 'pointer', userSelect: 'none' }}>{h}<span style={{ opacity: .6 }}>{arrow(gSort, k)}</span></th>)}
              </tr></thead>
              <tbody>
                {sortBy(view === 'license' ? d.byLicense : d.byDept, gSort).map((g) => (
                  <tr key={g.key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '7px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{g.key}</td>
                    <td style={td}>{g.users}</td><td style={td}>{g.hours.toLocaleString()}</td><td style={td}>{g.standard_hours.toLocaleString()}</td>
                    <td style={td}>{bar(g.util_pct)}</td>
                    <td style={{ ...td, color: g.LOW ? '#b91c1c' : undefined }}>{g.LOW || '-'}</td><td style={td}>{g.NORMAL || '-'}</td><td style={td}>{g.HIGH || '-'}</td>
                    <td style={{ ...td, color: g.OVER ? '#b45309' : undefined }}>{g.OVER || '-'}</td>
                    <td style={{ ...td, color: g.SHARED ? '#6d28d9' : undefined }}>{g.SHARED || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>Users ({users.length}){bandF !== 'ALL' ? ' - ' + UT_BAND[bandF][0] : ''}</div>
            {bandF !== 'ALL' && <button onClick={() => setBandF('ALL')} style={btn(false)}>Clear filter</button>}
            <div style={{ flex: 1 }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user, name, dept, license..."
              style={{ padding: '5px 10px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 8, width: 240 }} />
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
              <thead><tr style={{ background: '#f8fafc' }}>
                {[['User', 'username'], ['Name', 'full_name'], ['Dept', 'department'], ['License', 'license'], ['Hours', 'hours'], ['Standard', 'standard_hours'], ['Utilisation', 'util_pct'], ['Active days', 'active_days'], ['Peak terms', 'peak_terminals'], ['Capped', 'capped_sessions'], ['Band', 'band']].map(([h, k], i) =>
                  <th key={h} onClick={() => clickSort(setUSort)(k, i >= 4 && i <= 9)} style={{ ...th, textAlign: (i < 4 || i === 10) ? 'left' : 'right', cursor: 'pointer', userSelect: 'none' }}>{h}<span style={{ opacity: .6 }}>{arrow(uSort, k)}</span></th>)}
              </tr></thead>
              <tbody>
                {users.slice(0, 500).map((u) => (
                  <tr key={u.username} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{u.username}</td>
                    <td style={{ padding: '7px 10px', color: '#475569', whiteSpace: 'nowrap' }}>{u.full_name || '-'}</td>
                    <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{u.department || '-'}</td>
                    <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{u.license || '-'}</td>
                    <td style={td} title={'Raw (uncapped): ' + u.raw_hours + 'h'}>{u.hours}</td>
                    <td style={td} title={u.partial ? 'Counted from first logon' : ''}>{u.standard_hours}{u.partial ? '*' : ''}</td>
                    <td style={td}>{u.shared
                      ? <span title={u.util_pct + '% of one person'} style={{ color: '#6d28d9', fontWeight: 700 }}>≈ {u.person_eq} persons</span>
                      : bar(u.util_pct)}</td>
                    <td style={td}>{u.active_days}</td>
                    <td style={{ ...td, color: u.peak_terminals > 1 ? '#6d28d9' : undefined }} title={'avg ' + u.avg_terminals + ' terminals/day, ' + u.night_days + ' night-shift days'}>{u.peak_terminals}</td>
                    <td style={{ ...td, color: u.capped_sessions ? '#b45309' : undefined }}>{u.capped_sessions || '-'}</td>
                    <td style={{ padding: '7px 10px' }}><span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: UT_BAND[u.band][1], color: UT_BAND[u.band][2], whiteSpace: 'nowrap' }}>{UT_BAND[u.band][0].split(' ')[0]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>* standard counted from the user's first logon (new user). Hover Hours to see uncapped time, Peak terms for terminals/day. Capped = sessions longer than the session cap.</div>
        </>
      )}
    </>
  );
}
